import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Config,
  Logger,
  LogLevel,
  PixelStreaming,
} from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';
import useAsyncEffect from 'use-async-effect';
import { nanoid } from 'nanoid';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '../../../providers/MytaverseEventProvider/context';
import PixelStreamingLogsService from '@services/PixelStreamingLogsService';
import MytaStreamsService, {
  IMytaLease,
  ILeasePhase,
  NoFreeSlotError,
  isLeaseGone,
  isLeaseRefused,
  isSessionEnded,
  ISeatEvent,
  ISessionEnded,
  ISessionLife,
  MytaReleaseReason,
  MoveGoneError,
  IMoveProposal,
} from '@services/MytaStreamsService';
import { useNavigate } from 'react-router-dom';
import ROUTES from '@constants/routes';
import { MytaverseLogger } from '@helpers/logger';
import { forgetParticipantPosition } from '@helpers/participantPosition';
import { env } from 'env';
import { IEvent, IPixelStreamingClient } from '@interfaces/event';
import { IInitMessage } from '@interfaces/streaming';
import { AnalyticsType } from '@interfaces/analytics';
import { IUser } from '@interfaces/user';
import { IMytaStreamsContext, MytaLeaseStatus } from './interfaces';
import {
  AFK_ENDED_WHY,
  AFK_IDLE_MS,
  AFK_POLL_MS,
  IAfkPrompt,
  afkPromptAt,
  isAfk,
} from './afkWatch';
import {
  ENTRY_WATCH_POLL_MS,
  IEntryNotice,
  IEntryWatchState,
  MAX_ENTRY_RESCUES,
  describeEntry,
  entryVerdict,
  streamerReturnVerdict,
} from './entryWatch';
import {
  ISeatMoveProgress,
  ISeatMoveView,
  MOVE_INIT_RETRY_MS,
  MOVE_MAX_PEER_REBUILDS,
  MOVE_OFFER_TIMEOUT_MS,
  MOVE_RENEW_INTERVAL_MS,
  MOVE_WATCH_POLL_MS,
  SHADOW_PEER_STYLE,
  describeMoveView,
  isMoveEntryEvent,
  moveVerdict,
  proposalFromRenew,
  shouldStartMove,
} from './seatMove';
import {
  STREAM_PIXEL_BUDGET,
  capStreamResolution,
  createStreamResolutionCap,
  toResolutionCommand,
} from './streamResolutionCap';
import {
  buildCapacityIssuePayload,
  buildIncidentPayload,
  creditNetGap,
  EMPTY_NET_SNAPSHOT,
  IIncidentSlot,
  IMediaStatsReport,
  INetSnapshot,
  ISeatOutcome,
  IServerView,
  IVideoShape,
  MytaCutReason,
  MytaIssueKind,
  nextNetSnapshot,
  TICK_SUSPENSION_MS,
  videoShapeOf,
} from './helpers';
import { useReleaseHeldInput } from './heldInput';
import {
  DRAINING_RENEW_INTERVAL_MS,
  IDrainNotice,
  ISpotNotice,
  decideHandoff,
  describeGone,
  describeHandoffInProgress,
  describeSeatLost,
  describeNotice,
  noticeFromRenew,
} from './spotDrain';

// Retry ceiling is deliberately below the server's largest retryAfterSec hints (capacity_wait sends
// 60): at our user counts the extra polls are free, and a cold node that turns ready between hints
// should be seated within ~15 s, not a minute later.
// The floor exists so a hint of 0/1 cannot spin; it is NOT a policy knob. The server decides cadence
// per phase (3 s once a slot is nearly seatable, 5 s while a node boots, 20-30 s during a capacity
// drought), and a 5 s floor here silently discarded the fast end of that — a measured entry lost ~6 s
// sitting between two polls while its slot was already seatable.
const LEASE_RETRY_DELAY_MS = 10000;
const LEASE_RETRY_MIN_DELAY_MS = 2500;
const LEASE_RETRY_MAX_DELAY_MS = 15000;
const LEASE_RENEW_INTERVAL_MS = 45000;
// How long the "your session ended" screen is held before the page returns to the events list. Long
// enough to read the reason, short enough not to look like a hang.
const SESSION_ENDED_HOLD_MS = 6000;

// KI-37 media watchdog. The failure it exists for, measured 2026-07-29 (run
// hubcamp-20260729-105152-c1-warm-ohio, u2): the lease is granted, the signalling socket opens and NEVER
// closes or errors, and then nothing happens — no offer, no data channel, no INIT, no frame — while this
// provider renews the lease every 45 s forever and the UI sits on "Loading your experience". With
// `WaitForStreamer: true` the frontend waits for its `StreamerId` to appear in the streamer list, and if that
// streamer is not registered at that moment (a slot whose UE restarted — KI-2 login-map idle-quit — or one
// that has not finished registering) the wait is silent and unbounded. The server sees a valid seat and
// healthy renewals, so nothing reclaims it: only the client can notice that no media ever arrived.
//
// 15 s for the first frame: a warm seat frames in ~0.6 s and the slowest healthy path measured this month is
// ~9 s, so 15 s cannot false-positive on a working stream while still being far inside the ~20 s window in
// which the hub's own backend teardown (KI-36) would otherwise strand the user.
const FIRST_FRAME_TIMEOUT_MS = 15000;
// Mid-session freeze (KI-20 collateral: a neighbour slot's GPU fault froze a healthy slot's encoder). Frames
// stop while everything else stays nominal, so `framesDecoded` going flat is the only honest signal.
const MEDIA_STALL_TIMEOUT_MS = 10000;
// Two rebuilds on the same lease, then give the slot back. A third attempt on a slot that has failed twice is
// worth less than the ladder's chance to move the user somewhere else (its tiers re-run from scratch).
const MAX_PEER_REBUILDS = 2;
// A capacity wait is reported once per seat attempt, and only once it is clearly a wait rather than a poll.
const NO_CAPACITY_ISSUE_AFTER_MS = 30_000;
// How long the reconnect loader waits for a decoded frame AFTER the transport is already up before lifting
// itself anyway. Long enough that the normal path (stats arrive about once a second) always wins and the loader
// leaves on a real picture; short enough that a silent stats path costs a few seconds, not the whole session.
// Raised 4 s -> 25 s on 2026-09-14. Four seconds was measured against a TRANSPORT coming back, and on a
// reseat the transport is up long before the user is anywhere: the first frames of a replacement machine are
// its UE login screen, and lifting the loader onto those is what made a working handoff look like a hang. The
// loader now comes down when the user is confirmed in the venue (entryWatch.ts) — this is only the backstop
// that refuses to hold a loading screen over a stream forever, so it is sized to a venue load (8-20 s
// measured), not to a handshake.
const RECONNECT_SETTLE_MS = 25000;
// While the entry is still in flight, print one state line every this often. The operator asked to be able to
// watch an entry happen in real time — which node, which slot, which step, and how long each took — and the
// KI-37 hunt showed why: the failure IS silence, so a periodic line that says "still waiting, here is the
// state" is worth more than any post-mortem. It stops as soon as the first frame decodes.
const ENTRY_HEARTBEAT_MS = 5000;
// KI-70: the stream resolution is ours to send (MatchViewportRes is off), debounced so a window drag costs UE
// one Resolution command instead of a burst — every r.SetRes recreates the capture texture the zero-copy
// capturer reads, and the fence timeouts / Xid 31 in the KI-70 session followed exactly such bursts.
const RESOLUTION_CAP_DEBOUNCE_MS = 250;

// The name the control plane should show for whoever takes a seat: the profile's own display name, else the
// parts we hold. The hub is the only side that knows it — the lease API sees a Cognito uuid, and the node
// never sees the INIT's token — so a seat opened without it leaves every operator surface naming a uuid.
const seatUserName = (user?: IUser): string | undefined =>
  user?.name?.trim() ||
  [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
  undefined;

// A packing move in flight (seatMove.ts): the proposal, the second lease once claimed, the hidden peer, and every
// timer and listener that has to go when it ends — by a switch, an abort, or the page leaving.
interface IActiveMove {
  proposal: IMoveProposal;
  fromLeaseId: string;
  sessionKey: string;
  deadlineMs: number;
  progress: ISeatMoveProgress;
  lease: IMytaLease | null;
  stream: PixelStreaming | null;
  cleanup: Array<() => void>;
}

const moveView = (move: IActiveMove): ISeatMoveView => ({
  moveId: move.proposal.moveId,
  phase: move.progress.phase,
  fromLeaseId: move.fromLeaseId,
  to: move.proposal.to,
  sessionKey: move.sessionKey,
  deadline: move.deadlineMs,
  entered: move.progress.entered,
  frames: move.progress.frames,
});

interface IUseMytaStreamsSessionProps {
  enabled: boolean;
  currentEvent?: IEvent;
  pixelStreamingClient: IPixelStreamingClient | null;
  // `sessionKey` (the packing move): mint the INIT for a SECOND UE session without making that key the hub's
  // current one — the hub keeps filtering its websocket by the session the user is still looking at, and adopts
  // the new key (adoptSessionKey) only when the picture switches.
  initMessageHandler: (options?: {
    sessionKey?: string;
  }) => Promise<IInitMessage | null>;
  adoptSessionKey: (sessionKey: string) => void;
  initMessageSended: boolean;
  // Whether UE is talking to our backend. Read here so the meter cannot start on a peer whose UE never
  // came back: frames alone keep flowing from a UE rendering an error screen, and the watchdog sees no stall.
  ue5WebsocketConnected: boolean;
  setInitMessageSended: Dispatch<SetStateAction<boolean>>;
  setGameReadyToPlay: Dispatch<SetStateAction<boolean>>;
  gameSound: number;
  setMytaPixelStreaming: Dispatch<SetStateAction<PixelStreaming | null>>;
  // Needed for the reload rebind: the loader is gated on `teleportFinished`, which handleJoinedToEvent
  // resets to false on every entry and only a TELEPORT_FINISHED can set. After an F5 that event is in the
  // past — see adoptLease.
  setTeleportFinished: Dispatch<SetStateAction<boolean>>;
  // …and read back, because it is also the fastest proof that THIS seat put the user in the venue: UE
  // announces it the moment the venue is up. The entry ladder (entryWatch.ts) needs that answer to tell a
  // seat that is merely slow from one that is streaming a login map at the user.
  teleportFinished: boolean;
  // Reload rebind puts the participant back in the room the slot is actually in — see adoptLease. Takes the
  // slot's map, not a room: this provider knows which venue the seat is in, the event provider owns the
  // mapping to its own rooms and the participant state that `currentRoom` is derived from.
  restoreSeatedRoom: (mapName?: string) => void;
  // Passed in rather than read from the event context: this hook runs inside
  // MytaverseEventProvider, above the context it would consume.
  trackEventAnalytics: (type: AnalyticsType, payload?: any) => void;
}

export const useMytaStreamsSession = ({
  enabled,
  currentEvent,
  pixelStreamingClient,
  initMessageHandler,
  adoptSessionKey,
  initMessageSended,
  ue5WebsocketConnected,
  setInitMessageSended,
  setGameReadyToPlay,
  gameSound,
  setMytaPixelStreaming,
  setTeleportFinished,
  teleportFinished,
  restoreSeatedRoom,
  trackEventAnalytics,
}: IUseMytaStreamsSessionProps): IMytaStreamsContext => {
  const { userId, user, isDebugMode } = useMytaverse();

  const eventBuildUri = pixelStreamingClient?.clientParams?.buildUri;

  const [pixelStreaming, setPixelStreaming] = useState<PixelStreaming | null>(
    null,
  );
  const navigate = useNavigate();
  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
  const [dataChannelOpened, setDataChannelOpened] = useState(false);
  const [playStream, setPlayStream] = useState(false);
  // Entering is a user decision: the loader's Enter button is the only thing that sets this on a fresh
  // entry, and the resumer/adopt effect below refuses to start the world without it. A reload rebind onto
  // a session the user already entered once (resumedSessionRef) is the one path allowed to skip the click.
  const [userEntered, setUserEntered] = useState(false);
  const [mytaLoading, setMytaLoading] = useState(true);
  const [connectedSlot, setConnectedSlot] = useState('');
  const [lease, setLease] = useState<IMytaLease | null>(null);
  const [leaseStatus, setLeaseStatus] = useState<MytaLeaseStatus>('idle');
  const [leaseError, setLeaseError] = useState<string | null>(null);
  const [leasePhase, setLeasePhase] = useState<ILeasePhase | null>(null);
  // State, not a ref: billing latches it on the render before `playStream` drops.
  const [cutReason, setCutReason] = useState<MytaCutReason | null>(null);
  // Seat lifetime (myta-streams 2026-09-10). `sessionEnd` is the terminal verdict; `sessionLife` the
  // countdown every renew answer carries once a deadline is in sight.
  const [sessionEnd, setSessionEnd] = useState<ISessionEnded | null>(null);
  const [sessionLife, setSessionLife] = useState<ISessionLife | null>(null);
  // The AFK rule (afkWatch.ts): the live presence check, or null while nothing is being asked.
  const [afkPrompt, setAfkPrompt] = useState<IAfkPrompt | null>(null);
  // The entry ladder (entryWatch.ts): what this page is doing to get the user INTO the venue, in words. Held
  // as state because it is rendered, and set from every step of the way in — including the ones that used to
  // be silent, which is how a user ended up watching a login map for three and a half minutes on 2026-09-14.
  const [entryNotice, setEntryNotice] = useState<IEntryNotice | null>(null);
  // Whether THIS seat has actually put the user in the venue. Two independent proofs, whichever lands first:
  // UE's own TELEPORT_FINISHED (fast, but announced exactly once and only to whoever is attached), and the
  // server's `sessionReady` on a renew (slow, every 45 s, but true regardless of what this page received).
  // Per seat, never per page: it is cleared by `adoptLease` for every new lease.
  const enteredRef = useRef(false);
  const entryRef = useRef<IEntryWatchState>({
    seatedAt: null,
    initSentAt: null,
    nudged: false,
    rescues: 0,
  });
  // When the streamer this page wants went missing — the clock the 60 s return cap is measured on. Null
  // whenever the streamer is present, so the cap can never fire on a stream that is simply slow to frame.
  const streamerAwaySinceRef = useRef<number | null>(null);
  // The ladder's own messages outrank the ordinary narration below: once the page is nudging an INIT, waiting
  // out a UE restart, moving the user or giving up, that is what the screen must keep saying until the seat
  // changes or the user is in. Cleared by `adoptLease` and by the entry confirmation.
  const stickyEntryRef = useRef(false);
  const [target, setTarget] = useState<{
    signallingUrl: string;
    streamerId: string;
  } | null>(null);

  // KI-37: bumping this re-runs the [target] effect, whose cleanup disconnects the old instance before the
  // body builds a new one — the KI-22 one-peer-per-slot rule is satisfied by construction (close, then retry).
  const [peerRebuild, setPeerRebuild] = useState(0);
  // Re-runs the lease effect after we hand a bad slot back, so the ladder gets a fresh chance to place us.
  const [leaseAttempt, setLeaseAttempt] = useState(0);
  // Spot per client (myta-streams 2026-09-08): AWS may reclaim the SPOT box this seat sits on with a two-minute
  // notice. The lease API reports it on every renew; this page shows it in words, moves the user to a new seat
  // before the box dies (handoff), and says so — nothing about a reclaim is ever silent.
  const [drainNotice, setDrainNotice] = useState<IDrainNotice | null>(null);
  const [spotNotice, setSpotNotice] = useState<ISpotNotice | null>(null);
  const drainRef = useRef<IDrainNotice | null>(null);
  // One handoff per lease: set when this page decides to leave (or learns the box is gone), cleared when the next
  // lease is adopted. `movingRef` keeps the "moving you…" banner up until the new stream actually plays.
  const handoffRef = useRef(false);
  const movingRef = useRef(false);
  // The packing move (seatMove.ts, myta-streams D-58). `moveRef` is the move in flight; `declinedMovesRef` the
  // proposals the server refused, so a renew that keeps carrying one is not re-claimed every 45 s; `adoptPeerRef`
  // hands the pre-connected, already-framing PixelStreaming instance to the [target] effect, which would
  // otherwise build a fresh one and throw away the whole point of the make-before-break.
  const moveRef = useRef<IActiveMove | null>(null);
  const declinedMovesRef = useRef<Set<string>>(new Set());
  const adoptPeerRef = useRef<{
    stream: PixelStreaming;
    signallingUrl: string;
    streamerId: string;
  } | null>(null);
  const [seatMove, setSeatMove] = useState<ISeatMoveView | null>(null);
  const sessionEndRef = useRef<ISessionEnded | null>(null);
  // Re-runs the INIT effect while the player-session mint comes back empty — SM recreates a dead game
  // session in the background, so the payload must be retried; one silent null used to strand the user
  // on the login map forever.
  const [initAttempt, setInitAttempt] = useState(0);
  const rebuildsRef = useRef(0);
  // Whether this lease ever showed a picture. Latched because a rebuild resets
  // the frame counter, so the counter alone cannot answer it at the give-up.
  const everFramedRef = useRef(false);
  // The same fact as render state, because billing meters presence off it.
  // Whether the CURRENT peer is showing a picture — what billing meters off. Scoped per peer, unlike
  // `everFramedRef`: a rebuild means nobody sees anything until the new peer frames.
  const pictureLiveRef = useRef(false);
  const [pictureLive, setPictureLive] = useState(false);
  // The stream this page was showing is gone and we are rebuilding it — a spot reclaim handoff, a node that
  // died under the seat, the KI-37 watchdog rebuilding the peer. Deliberately NOT `pictureLive` inverted:
  // that one is per-peer and also waits on UE's websocket, so it is briefly false on a perfectly healthy
  // entry and would flash the loader over a stream that is starting fine. This is set only where the picture
  // is knowingly torn down, and cleared by the first frame of whatever replaces it.
  const [reconnecting, setReconnecting] = useState(false);
  // Mirrored so the stats handler can read it without taking it as a dep — `playStream` changing would
  // rebuild the watchdog and restart the very clocks it judges a dead slot by.
  const playStreamRef = useRef(false);
  // Leases this component already released on purpose. The [lease] effect's cleanup also releases (that is
  // what makes an in-app exit hand the seat back), so without this the watchdog's give-up path sent TWO
  // DELETEs for one lease — harmless to the server, but it puts a phantom second release into the lease-event
  // audit trail, which is the timeline we debug seat questions from.
  const releasedRef = useRef<Set<string>>(new Set());
  // Set when we re-attach to a slot whose UE is already in a venue. That UE is still holding the websocket
  // session of the page that is now gone — see the INIT effect, which has to move it onto this page's session.
  const staleUeSessionRef = useRef(false);
  // Same rebind, different consumer: the user entered this session before the reload, so the enter gate is
  // already satisfied and the adopt/resumer effect may start the world without a second click. Separate from
  // staleUeSessionRef because that one is consumed (and reset) by the INIT effect.
  const resumedSessionRef = useRef(false);
  // While the server is restarting the UE on our own seat there is deliberately no streamer, so "no frame
  // yet" is the expected state and must not be read as a dead slot — reclaiming here would hand away the very
  // seat the restart is preserving. How long that takes is not ours to guess: the signalling gateway
  // publishes the slot's live registration in every streamer list, so the wait is bound to that signal —
  // streamer absent = still coming up, streamer back = start judging it like any other entry.
  const ueRestartingRef = useRef(false);
  const ueRestartSawDropRef = useRef(false);
  // `null` until the first list arrives: before that its absence says nothing,
  // and reading it as "the streamer left" blames the node for a viewer's outage.
  const streamerPresentRef = useRef<boolean | null>(null);
  const mediaRef = useRef<{ frames: number; at: number }>({ frames: 0, at: 0 });
  // Diagnostics only, never a verdict: the browser sees frames stop but not why,
  // and these are the numbers that tell a dead transport from a stuck decoder.
  const netRef = useRef<INetSnapshot>({ ...EMPTY_NET_SNAPSHOT });
  // One clock for the whole entry, so every line answers "how long since the user asked to enter".
  const entryT0Ref = useRef<number>(Date.now());
  const leaseAtRef = useRef<number>(0);
  const stepRef = useRef<string>('boot');
  const wsStateRef = useRef<string>('-');
  // Latched separately from stepRef, which later steps overwrite: a failed ICE
  // state proves the transport died rather than being inferred from silence.
  const iceStateRef = useRef<string>('-');
  // Server-side facts latched per lease as they arrive and read at the verdict, never by the watchdog's clock.
  const seatOutcomeRef = useRef<ISeatOutcome | null>(null);
  const ueEventRef = useRef<ISeatEvent | null>(null);
  const serverViewRef = useRef<IServerView | null>(null);
  const videoRef = useRef<IVideoShape | null>(null);
  const firstPeerFramedRef = useRef(false);
  const rebuildsFramedRef = useRef(0);
  const leaseAttemptRef = useRef(0);
  const capacityWaitRef = useRef<{ since: number; reported: boolean } | null>(
    null,
  );

  useEffect(() => {
    leaseAttemptRef.current = leaseAttempt;
  }, [leaseAttempt]);

  useEffect(() => {
    playStreamRef.current = playStream;
  }, [playStream]);

  const mlog = useCallback((message: string) => {
    const t = ((Date.now() - entryT0Ref.current) / 1000).toFixed(1);
    MytaverseLogger.log(`MYTA_STREAMS [+${t}s] ${message}`);
  }, []);

  // Held in a ref so reporting an incident does not put the tracker in the
  // watchdog's deps, where a new websocket session would restart its clock.
  const trackRef = useRef(trackEventAnalytics);
  // Mirrored for the same reason: as a watchdog dep it would reset the counters an incident reads.
  const ue5ConnectedRef = useRef(ue5WebsocketConnected);

  useEffect(() => {
    trackRef.current = trackEventAnalytics;
  }, [trackEventAnalytics]);

  useEffect(() => {
    ue5ConnectedRef.current = ue5WebsocketConnected;
  }, [ue5WebsocketConnected]);

  // The same three events GameCast reports, so the crash/timeout/issue counters and a visit's end reason
  // stay provider-agnostic. Reads refs only, so it is stable and never restarts an effect's clock.
  const trackIncident = useCallback(
    (
      slot: IIncidentSlot,
      type: AnalyticsType,
      error: string,
      issueKind?: MytaIssueKind,
    ) => {
      trackRef.current(
        type,
        buildIncidentPayload({
          error,
          issueKind,
          slot,
          media: mediaRef.current,
          everFramed: everFramedRef.current,
          net: netRef.current,
          streamerPresent: streamerPresentRef.current,
          ueRestarting: ueRestartingRef.current,
          iceState: iceStateRef.current,
          signallingState: wsStateRef.current,
          step: stepRef.current,
          leaseAttempt: leaseAttemptRef.current + 1,
          rebuilds: rebuildsRef.current,
          firstPeerFramed: firstPeerFramedRef.current,
          rebuildsFramed: rebuildsFramedRef.current,
          seat: seatOutcomeRef.current,
          ueEvent: ueEventRef.current,
          server: serverViewRef.current,
          video: videoRef.current,
          now: Date.now(),
        }),
      );
    },
    [],
  );

  // Every line that describes progress carries the same identity block, because "it is stuck" is unusable
  // without knowing WHICH node and slot it is stuck on.
  const where = useCallback(
    () =>
      lease
        ? `node=${lease.nodeId} slot=${lease.slotIndex} streamer=${
            lease.streamerId
          } gw=${(() => {
            try {
              return new URL(lease.signallingUrl).host;
            } catch {
              return lease.signallingUrl;
            }
          })()}`
        : 'node=- slot=-',
    [lease],
  );

  const isPlayButtonPressedRef = useRef(false);
  const hoveringMouseIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const leaseRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const initRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const leaseIdRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      setPlayStream(false);
      setGameReadyToPlay(false);
      setConnectedSlot('');
      setInitMessageSended(false);
      setInitAttempt(0);
      setMytaPixelStreaming(null);
      if (hoveringMouseIntervalRef.current) {
        clearInterval(hoveringMouseIntervalRef.current);
        hoveringMouseIntervalRef.current = null;
      }
      if (initRetryTimeoutRef.current) {
        clearTimeout(initRetryTimeoutRef.current);
        initRetryTimeoutRef.current = null;
      }
    };
  }, []);

  // Mirrors the local instance onto the shared event context so
  // provider-agnostic UI (e.g. DevelopersTerminal) can reach it without
  // being mounted inside this provider's subtree.
  useEffect(() => {
    setMytaPixelStreaming(pixelStreaming);
  }, [pixelStreaming, setMytaPixelStreaming]);

  useEffect(() => {
    if (!enabled || !currentEvent) {
      return;
    }

    if (isDebugMode) {
      mlog(
        'STEP editor debug mode is on — no slot is leased and no peer is built; the INIT payload from the Developers Terminal has to be pasted into a local UE editor, which then drives this page over the dashboard websocket',
      );
      return;
    }

    if (!MytaStreamsService.isLeaseApiConfigured()) {
      setLeaseStatus('unconfigured');
      setLeaseError(
        'MYTA lease API is not configured (REACT_APP_MYTA_STREAMS_LEASE_API_URL / REACT_APP_MYTA_STREAMS_LEASE_API_TOKEN)',
      );
      MytaverseLogger.log('MYTA_STREAMS lease API is not configured');
      return;
    }

    let cancelled = false;

    const adoptLease = (adopted: IMytaLease, source: 'lease' | 'rebind') => {
      leaseAtRef.current = Date.now();
      stepRef.current = 'lease-granted';
      // A new seat starts with a clean spot slate; the "moving you…" banner (movingRef) stays until it plays.
      handoffRef.current = false;
      drainRef.current = null;
      setDrainNotice(null);
      mlog(
        `STEP lease ${source === 'rebind' ? 'REBOUND' : 'GRANTED'} ${
          adopted.leaseId
        } — node=${adopted.nodeId} slot=${adopted.slotIndex} streamer=${
          adopted.streamerId
        } url=${adopted.signallingUrl} build=${
          (adopted.buildUri ?? '').split('/').pop() ?? '-'
        }${
          adopted.sessionReady
            ? ` sessionReady=true map=${
                adopted.mapName ?? '-'
              } (UE already announced CLIENT_READY before this page existed — it will not repeat it)`
            : ''
        }`,
      );
      MytaStreamsService.saveStoredLease(currentEvent.eventId, adopted);
      // Reload rebind: settle the loader ourselves. The loader waits on `teleportFinished`, which
      // handleJoinedToEvent resets to false on every entry and which ONLY a TELEPORT_FINISHED can set —
      // but UE announces CLIENT_READY/TELEPORT_FINISHED exactly once, to whoever is attached at that
      // instant. An F5 attaches a NEW peer to a session that is already in the venue, so the announcement
      // is in the past and never repeats: measured 2026-08-11, 10/10 reload rounds sat on
      // "Loading your experience 95%" while frames decoded behind it at a clean 30 fps.
      // `sessionReady` is the lease API telling us exactly that (slot row's mapName is a real venue, not a
      // staging map), so it is the correct substitute for the event we will never receive.
      // Deliberately NOT applied to source === 'lease': a fresh grant boots UE, which will announce for
      // real, and short-circuiting there would drop the loader before the venue is up.
      if (adopted.restarting) {
        // The seat is ours and stays ours; the UE on it is being replaced so this page gets its own session.
        // Nothing to settle by hand — the fresh UE announces CLIENT_READY/JOINED_TO_ROOM for real, exactly
        // like a first entry, which is the whole point of paying for the restart.
        ueRestartingRef.current = true;
        ueRestartSawDropRef.current = false;
        streamerPresentRef.current = false;
        // The restart clock starts here, and it is bounded (entryWatch.STREAMER_RETURN_MS): the wait used to
        // be tied to the streamer list alone, which is right while a UE is coming back and unbounded when it
        // never does. A restarting UE is also, by definition, not in the venue any more — so the entry proof
        // goes with it and the loader is armed to stay up until the new session is really in.
        streamerAwaySinceRef.current = Date.now();
        enteredRef.current = false;
        entryRef.current = {
          ...entryRef.current,
          seatedAt: Date.now(),
          initSentAt: null,
          nudged: false,
        };
        setTeleportFinished(false);
        stickyEntryRef.current = true;
        setEntryNotice(describeEntry({ stage: 'engine-restart' }));
        mlog(
          `STEP seat kept, UE RESTARTING on it (its session belonged to the page that disconnected) — waiting for streamer ${
            adopted.streamerId
          } to re-register${
            adopted.etaSec ? ` (server eta ~${adopted.etaSec}s)` : ''
          }`,
        );
      }

      if (source === 'rebind' && adopted.sessionReady) {
        staleUeSessionRef.current = true;
        resumedSessionRef.current = true;
        setTeleportFinished(true);
        // …and put the room back. PixelStreamingWrapper renders the whole stream container only when
        // `currentRoom` is set, and `handleJoinedToEvent` wipes the participant's roomId on every entry —
        // it is restored by a JOINED_TO_ROOM that, like CLIENT_READY, was sent before this page existed.
        // So after an F5 the container is never mounted and the user gets a blank page even once the
        // loader is dismissed (measured live 2026-08-12: overlay gone, `mytaStreamsWrapper` absent while the
        // slot streamed the museum at 30 fps). The lease knows which venue the seat is in; the event provider
        // owns the rest, because `currentRoom` is derived from the participant and cannot be written directly.
        restoreSeatedRoom(adopted.mapName);
      }
      setLeaseStatus('active');
      setLeaseError(null);
      setLeasePhase(null);
      // Re-adopting the SAME leaseId (a dep-driven effect re-run rebinding in place) must not create
      // a new lease object: the [lease] effect's cleanup releases the OLD object's leaseId, and with
      // an unchanged id it would release the lease we just rebound — seat freed under a live stream.
      if (leaseIdRef.current === adopted.leaseId) {
        return;
      }
      leaseIdRef.current = adopted.leaseId;
      // A new seat has not put anyone anywhere yet. The entry clocks start at nothing and the venue proof is
      // dropped — `rescues` is the one field that survives, because it counts seats across the whole visit and
      // is what stops the page trying machines forever. `teleportFinished` is re-armed for the same reason: it
      // is the loader's gate, it stays true from the seat we just left, and a reseat that inherits it drops the
      // loading screen onto a UE that is still on its login map (measured on the 2026-09-14 spot handoff).
      enteredRef.current = false;
      entryRef.current = {
        seatedAt: Date.now(),
        initSentAt: null,
        nudged: false,
        rescues: entryRef.current.rescues,
      };
      streamerAwaySinceRef.current = null;
      stickyEntryRef.current = false;
      if (!(source === 'rebind' && adopted.sessionReady)) {
        setTeleportFinished(false);
      }
      // The rebuild budget is per LEASE, not per page: a fresh slot deserves its own two attempts.
      rebuildsRef.current = 0;
      everFramedRef.current = false;
      pictureLiveRef.current = false;
      setPictureLive(false);
      mlog(
        `picture live reset — adopting lease ${adopted.leaseId} — ${where()}`,
      );
      firstPeerFramedRef.current = false;
      rebuildsFramedRef.current = 0;
      seatOutcomeRef.current = null;
      ueEventRef.current = null;
      videoRef.current = null;
      serverViewRef.current = {
        mapName: adopted.mapName ?? null,
        sessionReady: adopted.sessionReady ?? null,
        at: Date.now(),
      };
      capacityWaitRef.current = null;
      setLease(adopted);
      setTarget({
        signallingUrl: adopted.signallingUrl,
        streamerId: adopted.streamerId,
      });
    };

    // Reload rebind: an F5 lands here with the tab's lease still alive server-side (beforeunload no
    // longer releases it). Renew validates the seat is still ours; on success we reconnect to the SAME
    // slot in seconds — the UE process never restarted (watchdog counts 1 left / 1 joined as a
    // reconnect, strictly-greater rule). A failed renew (seat TTL-reclaimed or repurposed) just falls
    // through to a fresh lease.
    const tryRebind = async (): Promise<boolean> => {
      const stored = MytaStreamsService.loadStoredLease(currentEvent.eventId);
      if (!stored) {
        return false;
      }

      // The event's build may have been rebound while we were away — a stored lease for another
      // build must not be resumed (the user would land in the wrong world).
      if (
        eventBuildUri &&
        stored.buildUri &&
        stored.buildUri !== eventBuildUri
      ) {
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
        MytaverseLogger.log(
          `MYTA_STREAMS stored lease ${stored.leaseId} is for another build — taking a fresh lease`,
        );
        return false;
      }

      const renewed = await MytaStreamsService.renewLease(stored.leaseId, {
        rebind: true,
      });
      if (cancelled) {
        return true;
      }
      if (renewed === 'network_error') {
        // A transient blip must not burn the stored lease — the fresh-lease path will run now, and
        // if it also fails the next retry can still rebind.
        MytaverseLogger.log(
          `MYTA_STREAMS stored lease ${stored.leaseId} renew hit a network error — keeping it, trying a fresh lease`,
        );
        return false;
      }
      // Every definitive refusal, not just the bare `null`: spreading one over the
      // stored lease would adopt a seat that no longer exists.
      if (!renewed || isLeaseGone(renewed) || isLeaseRefused(renewed)) {
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
        MytaverseLogger.log(
          `MYTA_STREAMS stored lease ${stored.leaseId} not renewable — taking a fresh lease`,
        );
        return false;
      }
      if (isSessionEnded(renewed)) {
        // A reload of a visit the server has already ended. The stored lease is not merely stale, the
        // VISIT is over — so this must not fall through to a fresh lease the way the two reseat answers
        // below do, or an F5 would be a way to walk straight past the cap.
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
        MytaverseLogger.log(
          `MYTA_STREAMS stored lease ${stored.leaseId} — session already ended (${renewed.cause})`,
        );
        await endSession(null, renewed);
        return true;
      }
      if (isLeaseGone(renewed) || isLeaseRefused(renewed)) {
        // Both say the seat is gone but the visit is not: the box died, or the row moved on. They used to
        // fall through into the adopt below, which spread a verdict object over the stored lease and
        // produced a phantom seat with no signalling url — a fresh lease is what both actually want.
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
        MytaverseLogger.log(
          `MYTA_STREAMS stored lease ${stored.leaseId} is gone (${
            isLeaseGone(renewed) ? renewed.cause ?? 'node_gone' : renewed.error
          }) — taking a fresh lease`,
        );
        return false;
      }

      adoptLease({ ...stored, ...renewed }, 'rebind');
      return true;
    };

    const requestLease = async () => {
      // Keep `waiting_slot` (and its phase card) up during retries — flipping back to `requesting`
      // per attempt made the loading HUD flash stage 1 and reset its countdown every 10-15 s.
      setLeaseStatus((prev) => (prev === 'waiting_slot' ? prev : 'requesting'));
      stepRef.current = 'lease-requesting';
      mlog(
        `STEP requesting a slot — event=${currentEvent.eventId} build=${
          (eventBuildUri ?? '').split('/').pop() ?? '-'
        }`,
      );

      try {
        if (await tryRebind()) {
          return;
        }

        console.log('MYTA_TIMING t_acquireCall');
        const acquiredLease = await MytaStreamsService.acquireLease({
          eventId: currentEvent.eventId,
          userId: userId as string,
          userEmail: user?.email,
          userName: seatUserName(user),
          buildUri: eventBuildUri,
          attempt: leaseAttempt + 1,
        });

        if (cancelled) {
          MytaStreamsService.releaseLease(acquiredLease.leaseId, {
            reason: 'exit',
          });
          return;
        }

        adoptLease(acquiredLease, 'lease');
      } catch (e) {
        if (cancelled) {
          return;
        }

        let retryDelayMs = LEASE_RETRY_DELAY_MS;

        if (e instanceof NoFreeSlotError) {
          setLeaseStatus('waiting_slot');
          setLeaseError('No free slot available — retrying');
          setLeasePhase(e.details ?? null);
          // §10: capacity running out is the likeliest real incident and used to report nothing. Once per
          // attempt, once the wait is a wait — never per poll.
          const waiting = capacityWaitRef.current ?? {
            since: Date.now(),
            reported: false,
          };
          capacityWaitRef.current = waiting;
          const waitedMs = Date.now() - waiting.since;
          if (!waiting.reported && waitedMs >= NO_CAPACITY_ISSUE_AFTER_MS) {
            waiting.reported = true;
            trackRef.current(
              AnalyticsType.EventSessionIssue,
              buildCapacityIssuePayload({
                details: e.details ?? null,
                leaseAttempt: leaseAttempt + 1,
                waitedMs,
                eventId: currentEvent.eventId,
              }),
            );
          }
          if (e.details?.retryAfterSec) {
            retryDelayMs = Math.min(
              Math.max(
                e.details.retryAfterSec * 1000,
                LEASE_RETRY_MIN_DELAY_MS,
              ),
              LEASE_RETRY_MAX_DELAY_MS,
            );
          }
          // provisioning:false = the server is NOT working on capacity (unsupported build, pool
          // ceiling, another live session) — hammering it every 10 s is noise. Slow-poll so a
          // rebound build / freed node / closed other-tab still recovers without a reload.
          if (e.details?.provisioning === false) {
            retryDelayMs = Math.max(
              retryDelayMs,
              (e.details?.retryAfterSec ?? 60) * 1000,
            );
          }
          stepRef.current = `lease-wait:${e.details?.phase ?? '-'}`;
          mlog(
            `STEP server is provisioning — phase=${
              e.details?.phase ?? '-'
            } eta=${e.details?.etaSec ?? '-'}s provisioning=${
              e.details?.provisioning ?? '-'
            } node=${
              (e.details as { nodeId?: string })?.nodeId ?? '-'
            } next poll in ${Math.round(retryDelayMs / 1000)}s${
              e.details?.hint ? ` — ${e.details.hint}` : ''
            }`,
          );
        } else {
          setLeaseStatus('error');
          setLeaseError((e as Error).message);
          setLeasePhase(null);
          stepRef.current = 'lease-error';
          mlog(
            `STEP lease request FAILED: ${
              (e as Error).message
            } — retrying in ${Math.round(retryDelayMs / 1000)}s`,
          );
        }

        leaseRetryTimeoutRef.current = setTimeout(requestLease, retryDelayMs);
      }
    };

    requestLease();

    return () => {
      cancelled = true;
      if (leaseRetryTimeoutRef.current) {
        clearTimeout(leaseRetryTimeoutRef.current);
        leaseRetryTimeoutRef.current = null;
      }
    };
  }, [
    enabled,
    currentEvent?.eventId,
    userId,
    eventBuildUri,
    leaseAttempt,
    isDebugMode,
  ]);

  // The one exit shared by the KI-37 watchdog and the spot handoff: drop the current seat and ask the ladder for
  // another. The stored lease is cleared FIRST so the re-request cannot rebind to the seat being abandoned, and the
  // release happens BEFORE the new request so the fresh lease never races a seat we are about to hand back (at the
  // pool ceiling the ladder would otherwise answer capacity_wait for it). `release: false` when the server already
  // freed the seat (node gone); `reason: 'spot-handoff'` names the move off a reclaimed spot box in the session record.
  // The activity report the renew carries (myta-streams api/seat-policy.ts). Two facts, both about THIS
  // page: when a real human gesture last happened, and whether the tab is backgrounded right now.
  //
  // Listeners are on `window` with `capture: true` because the stream takes every pointer and key event
  // for itself — the pixel-streaming library attaches its own handlers to the video element and stops
  // propagation, so a bubbling listener on document sees a user who is playing as perfectly idle. The
  // capture phase runs before any of that.
  //
  // `lastInputAt` starts at mount rather than at 0: a page that has just opened has a human in front of
  // it by construction, and a seat is never idle-ended for the seconds before the first mouse move.
  const lastInputRef = useRef<number>(Date.now());
  const hiddenRef = useRef<boolean>(
    typeof document !== 'undefined' && document.visibilityState === 'hidden',
  );
  useEffect(() => {
    const seen = () => {
      lastInputRef.current = Date.now();
    };
    const events = [
      'pointerdown',
      'pointermove',
      'keydown',
      'wheel',
      'touchstart',
    ] as const;
    for (const type of events) {
      window.addEventListener(type, seen, { capture: true, passive: true });
    }
    const onVisibility = () => {
      hiddenRef.current = document.visibilityState === 'hidden';
      // Coming BACK is itself a gesture — the user switched to this tab. Without this the idle clock
      // would keep running from the last input before they left.
      if (!hiddenRef.current) {
        seen();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      for (const type of events) {
        window.removeEventListener(type, seen, { capture: true });
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    sessionEndRef.current = sessionEnd;
  }, [sessionEnd]);

  // Give a move up: tear the hidden peer down, hand the second seat back as `move-failed` (unless the server
  // already took it), and stay exactly where the user is — they never saw any of it. Every exit from the
  // current seat (a reseat, the visit ending, the page leaving) calls this first, because a second seat nobody
  // is switching to is a GPU slot billing for nothing.
  const abortMove = useCallback(
    (why: string, opts: { releaseSeat: boolean } = { releaseSeat: true }) => {
      const move = moveRef.current;
      if (!move) {
        return;
      }
      moveRef.current = null;
      // Whatever ended it, this proposal is not tried again: the victim's renew keeps carrying `move` until the
      // server's tick drops it, and a claim inside that gap would buy a second seat with seconds to live.
      declinedMovesRef.current.add(move.proposal.moveId);
      for (const fn of move.cleanup) {
        try {
          fn();
        } catch {
          /* a listener already gone */
        }
      }
      if (move.stream) {
        try {
          move.stream.disconnect();
        } catch (e) {
          console.warn('Failed to disconnect the move peer', e);
        }
        move.stream.videoElementParent.remove();
      }
      if (move.lease && opts.releaseSeat) {
        releasedRef.current.add(move.lease.leaseId);
        void MytaStreamsService.releaseLease(move.lease.leaseId, {
          reason: 'move-failed',
          keepalive: true,
        });
      }
      setSeatMove(null);
      mlog(
        `MOVE ${move.proposal.moveId} ABORTED while ${
          move.progress.phase
        } — ${why}${
          move.lease
            ? opts.releaseSeat
              ? ` — released the new seat ${move.lease.leaseId} (?reason=move-failed)`
              : ` — the new seat ${move.lease.leaseId} was already gone`
            : ''
        } — staying on ${move.fromLeaseId}`,
      );
    },
    [mlog],
  );

  useEffect(() => () => abortMove('the page left'), [abortMove]);

  // The fast venue proof for a move: UE announces TELEPORT_FINISHED / CLIENT_READY exactly once, on the dashboard
  // websocket, under the session key it was INIT-ed with — which during a move is NOT the hub's current key, so
  // every ordinary handler skips it. DashboardWebsocket feeds every message here (useHandleSeatMoveSessionMsg).
  const noteSessionEvent = useCallback(
    (action: unknown, sessionKey: unknown) => {
      const move = moveRef.current;
      if (
        !move ||
        move.progress.entered ||
        !isMoveEntryEvent(action, sessionKey, move.sessionKey)
      ) {
        return;
      }
      move.progress = { ...move.progress, entered: true };
      setSeatMove(moveView(move));
      mlog(
        `MOVE ${move.proposal.moveId} — UE announced ${String(
          action,
        )} on the new session: the venue is up there (${
          move.progress.frames
        } frames decoded so far)`,
      );
    },
    [mlog],
  );

  // The terminal exit — abandonSlot's sibling, and the difference between them is the whole point of the
  // 403: abandonSlot bumps `leaseAttempt`, which re-runs the acquire effect and takes a NEW seat, which is
  // right for a dead machine and exactly wrong for a visit the server has ended. This one lets the seat go
  // and stops, then returns the user to the events list where a new visit is a deliberate act.
  const endSession = useCallback(
    async (dead: IMytaLease | null, verdict: ISessionEnded) => {
      abortMove('the visit ended');
      if (dead && currentEvent?.eventId) {
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
      }
      // The visit is over, so the spot the user stood in is not where the next one starts.
      forgetParticipantPosition();
      if (dead) {
        releasedRef.current.add(dead.leaseId);
      }
      leaseIdRef.current = null;
      setSessionEnd(verdict);
      setSessionLife(null);
      setDataChannelOpened(false);
      setPlayStream(false);
      setInitMessageSended(false);
      setTarget(null);
      setLease(null);
      setLeaseStatus('ended');
      // Nothing is coming back — the visit is over. Leaving this set would put a "reconnecting you" loader
      // over the last six seconds before the page returns to the events list.
      setReconnecting(false);
    },
    [currentEvent?.eventId, setInitMessageSended, abortMove],
  );

  // The seat is gone server-side the moment the 403 lands, so the page holds the ended screen only long
  // enough to be read, then leaves. Navigating immediately would look like a crash.
  useEffect(() => {
    if (!sessionEnd) {
      return;
    }
    const t = setTimeout(
      () => navigate(ROUTES.SELECT_EVENT),
      SESSION_ENDED_HOLD_MS,
    );
    return () => clearTimeout(t);
  }, [sessionEnd, navigate]);

  // The AFK rule's own ending (afkWatch.ts). Unlike the 403, the seat is still ours here — nobody has
  // refused anything — so this page has to hand it back itself before it takes the terminal exit. That
  // release IS the feature: without it the box keeps billing until the lease TTL lapses.
  const endForAfk = useCallback(
    async (dead: IMytaLease) => {
      // Claimed up front, exactly like giveUpSlot: the release is a round trip that the timer behind it
      // keeps ticking through, and the renew effect's own cleanup reads the same set to decide whether to
      // send the unmount release.
      if (releasedRef.current.has(dead.leaseId)) {
        return;
      }
      releasedRef.current.add(dead.leaseId);
      mlog(
        `AFK — ${AFK_ENDED_WHY}; releasing the seat and returning to the events list — ${where()}`,
      );
      seatOutcomeRef.current = {
        outcome: 'ended:afk',
        cause: 'idle',
        reason: AFK_ENDED_WHY,
        at: Date.now(),
      };
      trackIncident(
        dead,
        AnalyticsType.EventSessionTimeout,
        `session ended — afk (${AFK_ENDED_WHY})`,
      );
      await MytaStreamsService.releaseLease(dead.leaseId, { reason: 'afk' });
      setAfkPrompt(null);
      await endSession(dead, {
        ended: true,
        cause: 'idle',
        why: AFK_ENDED_WHY,
      });
    },
    [endSession, mlog, trackIncident, where],
  );

  // Answering moves `lastInputRef`, which is both clocks at once: this page's, and — on the very next
  // renew — the server's. Nothing else is needed to extend the visit.
  const confirmPresence = useCallback(() => {
    lastInputRef.current = Date.now();
    setAfkPrompt(null);
    mlog(`AFK — presence confirmed, the seat stays — ${where()}`);
  }, [mlog, where]);

  // Ask. The poll runs only while a seat is actually held and no question is on screen, which is what
  // LATCHES the prompt: once it is up, a stray mouse move is not an answer. The whole point of the check
  // is a human deliberately saying they are there, and the cat-on-the-keyboard case is precisely the one
  // that would otherwise keep a forgotten tab on a GPU all night.
  useEffect(() => {
    if (!lease || afkPrompt || sessionEnd) {
      return undefined;
    }
    const poll = setInterval(() => {
      const now = Date.now();
      if (!isAfk(lastInputRef.current, now)) {
        return;
      }
      setAfkPrompt(afkPromptAt(now));
      mlog(
        `AFK — no input for ${Math.round(
          AFK_IDLE_MS / 60000,
        )} min, asking whether the user is still there — ${where()}`,
      );
    }, AFK_POLL_MS);
    return () => clearInterval(poll);
  }, [lease, afkPrompt, sessionEnd, mlog, where]);

  // Then act. A timer rather than a poll, so the seat goes back on the second the countdown promised.
  useEffect(() => {
    if (!lease || !afkPrompt || sessionEnd) {
      return undefined;
    }
    const t = setTimeout(() => {
      void endForAfk(lease);
    }, Math.max(0, afkPrompt.deadline - Date.now()));
    return () => clearTimeout(t);
  }, [lease, afkPrompt, sessionEnd, endForAfk]);

  // A seat that goes away for any other reason takes the question with it. Without this a prompt raised
  // just before a reseat would hang over the new seat with a deadline belonging to the old one — and a
  // reseat is exactly when it can happen, since an idle page is what the node-gone path finds.
  useEffect(() => {
    if (!lease) {
      setAfkPrompt(null);
    }
  }, [lease]);

  const abandonSlot = useCallback(
    async (
      dead: IMytaLease,
      release: false | { reason?: MytaReleaseReason },
    ) => {
      abortMove('the current seat is being handed back');
      if (currentEvent?.eventId) {
        MytaStreamsService.clearStoredLease(currentEvent.eventId);
      }
      releasedRef.current.add(dead.leaseId);
      if (release) {
        try {
          await MytaStreamsService.releaseLease(dead.leaseId, release);
        } catch (e) {
          console.warn('Failed to release a myta lease being abandoned', e);
        }
      }
      rebuildsRef.current = 0;
      leaseIdRef.current = null;
      setDataChannelOpened(false);
      setPlayStream(false);
      setInitMessageSended(false);
      setTarget(null);
      setLease(null);
      setLeaseStatus('requesting');
      // `connectedSlot` only feeds the cinema's phase, so resetting it is free. `mytaLoading` is NOT: it also
      // gates PixelStreamingWrapper's mount effect, and `videoInitialized` — the one event that clears it — is
      // one-shot per instance and can land before the listeners attach (the same race the playStream ADOPT poll
      // exists for). Raising it here bought a nicer progress bar and risked stranding the new video element
      // outside the DOM for good, so it stays where the first entry left it.
      setConnectedSlot('');
      // A seat handed back before the world ever came up is not a reconnect, it is the entry ladder still
      // trying — and calling it "reconnecting you" to a user who has never been in would be a lie.
      setReconnecting(isPlayButtonPressedRef.current);
      setLeaseAttempt((n) => n + 1);
    },
    [currentEvent?.eventId, setInitMessageSended, abortMove],
  );

  // The switch — the one cut of a packing move. Everything the user sees changes in a single React commit: the
  // pre-connected, already-framing hidden peer becomes the visible one (adoptPeerRef → the [target] effect adopts
  // it instead of building a new connection, and its cleanup disconnects the old peer), input and sound are turned
  // on for it, the hub's websocket identity follows the picture (adoptSessionKey), lease B becomes THE lease, and
  // only then is lease A released with `?reason=moved`. Nothing here raises the loader or clears `playStream`:
  // the venue is already up and decoding on the new peer, which is what the two proofs guaranteed.
  const switchToMovedSeat = useCallback(
    (move: IActiveMove, why: string) => {
      const next = move.lease;
      const stream = move.stream;
      if (!next || !stream || moveRef.current !== move) {
        return;
      }
      const from = move.fromLeaseId;
      const id = move.proposal.moveId;
      move.progress = { ...move.progress, phase: 'switching' };
      mlog(
        `MOVE ${id} SWITCHING — ${why}: ${from} → ${next.leaseId} (${next.nodeId}/${next.slotIndex} streamer=${next.streamerId}); one cut, no loader`,
      );
      for (const fn of move.cleanup) {
        try {
          fn();
        } catch {
          /* a listener already gone */
        }
      }
      moveRef.current = null;

      stream.config.setSettings({
        KeyboardInput: true,
        MouseInput: true,
        TouchInput: true,
        GamepadInput: true,
        HoveringMouse: true,
      });
      const element = stream.videoElementParent;
      for (const key of Object.keys(SHADOW_PEER_STYLE)) {
        element.style.setProperty(
          key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`),
          '',
        );
      }
      element.style.width = '100%';
      element.style.height = '100%';
      element.removeAttribute('data-myta-shadow-peer');
      const video = element.querySelector('video');
      if (video) {
        video.muted = false;
      }
      adoptPeerRef.current = {
        stream,
        signallingUrl: next.signallingUrl,
        streamerId: next.streamerId,
      };

      adoptSessionKey(move.sessionKey);

      // Lease A is released by THIS page with the reason that says why — claimed here so the [lease] effect's
      // cleanup, which runs when `lease` changes below, does not send its own `exit` for it.
      releasedRef.current.add(from);
      leaseIdRef.current = next.leaseId;
      leaseAtRef.current = Date.now();
      stepRef.current = 'moved';
      handoffRef.current = false;
      movingRef.current = false;
      drainRef.current = null;
      rebuildsRef.current = 0;
      everFramedRef.current = true;
      firstPeerFramedRef.current = true;
      rebuildsFramedRef.current = 0;
      seatOutcomeRef.current = null;
      ueEventRef.current = null;
      videoRef.current = null;
      serverViewRef.current = {
        mapName: next.mapName ?? null,
        sessionReady: next.sessionReady ?? null,
        at: Date.now(),
      };
      // The user IS in the venue on this seat — that was the first proof. The entry ladder stays satisfied, the
      // loader stays down, and the media watchdog starts its clocks on a peer that is already decoding.
      enteredRef.current = true;
      stickyEntryRef.current = false;
      streamerAwaySinceRef.current = null;
      ueRestartingRef.current = false;
      ueRestartSawDropRef.current = false;
      entryRef.current = {
        ...entryRef.current,
        seatedAt: Date.now(),
        initSentAt: Date.now(),
        nudged: false,
      };
      setDrainNotice(null);
      setSpotNotice(null);
      setSessionLife(null);
      setEntryNotice(null);
      setInitMessageSended(true);
      setDataChannelOpened(true);
      setConnectedSlot(next.streamerId);
      setLease(next);
      setTarget({
        signallingUrl: next.signallingUrl,
        streamerId: next.streamerId,
      });
      setSeatMove(null);

      void MytaStreamsService.releaseLease(from, { reason: 'moved' });
      if (userId && currentEvent) {
        PixelStreamingLogsService.createPixelStreamingLog({
          participantId: userId,
          eventId: currentEvent.eventId,
          timestamp: Date.now(),
          pixelStreamingProvider: 'MYTA_STREAMS',
          pixelStreamingProviderData: { id: next.streamerId },
        }).catch((e) =>
          console.warn('Failed to create myta pixel streaming log', e),
        );
      }
      mlog(
        `MOVE ${id} DONE — released ${from} (?reason=moved); the visit continues on ${next.leaseId} — node=${next.nodeId} slot=${next.slotIndex} streamer=${next.streamerId}`,
      );
    },
    [adoptSessionKey, currentEvent, userId, mlog, setInitMessageSended],
  );

  // The move, from the proposal to the verdict. Claim the held seat, open a SECOND Pixel Streaming connection to it
  // in a hidden, muted, input-less element, INIT that UE under its own websocket session key, and wait for both
  // proofs — the venue announced on that session (noteSessionEvent) or by lease B's renew (`sessionReady`), AND
  // frames decoding on the new peer. The user keeps the first stream the whole time. Everything that could go
  // wrong ends in `abortMove`, which hands the second seat back and changes nothing on screen.
  const beginMove = useCallback(
    async (proposal: IMoveProposal, from: IMytaLease) => {
      const id = proposal.moveId;
      const to = proposal.to;
      const verdict = shouldStartMove({
        inFlight: !!moveRef.current,
        declined: declinedMovesRef.current.has(id),
        entered: enteredRef.current,
        playing: playStreamRef.current,
        handoff: handoffRef.current,
        draining: drainRef.current?.kind === 'interrupt',
        ended: !!sessionEndRef.current,
      });
      if (!verdict.start) {
        if (!moveRef.current && !declinedMovesRef.current.has(id)) {
          mlog(
            `MOVE ${id} proposed (→ ${to.nodeId}/${to.slotIndex}) but not started: ${verdict.why}`,
          );
        }
        return;
      }
      if (!currentEvent || !userId) {
        return;
      }

      const deadlineMs = Date.parse(proposal.deadline);
      const sessionKey = nanoid(20);
      const move: IActiveMove = {
        proposal,
        fromLeaseId: from.leaseId,
        sessionKey,
        deadlineMs,
        progress: {
          phase: 'claiming',
          initSentAt: null,
          nudged: false,
          entered: false,
          frames: 0,
        },
        lease: null,
        stream: null,
        cleanup: [],
      };
      moveRef.current = move;
      const publish = () => setSeatMove(moveView(move));
      publish();
      mlog(
        `MOVE ${id} PROPOSED by the pool (economy${
          proposal.savesUsdPerHour !== undefined
            ? `, saves $${proposal.savesUsdPerHour.toFixed(2)}/h`
            : ''
        }) — ${from.nodeId}/${from.slotIndex} → ${to.nodeId}/${
          to.slotIndex
        } streamer=${to.streamerId}, deadline in ${Math.round(
          (deadlineMs - Date.now()) / 1000,
        )} s — claiming the held seat; the user stays on the current stream until the new one is in the venue and framing`,
      );

      let claimed: IMytaLease;
      try {
        claimed = await MytaStreamsService.claimMoveSeat({
          eventId: currentEvent.eventId,
          userId,
          userEmail: user?.email,
          userName: seatUserName(user),
          buildUri: eventBuildUri,
          attempt: leaseAttemptRef.current + 1,
          moveId: id,
          fromLeaseId: from.leaseId,
        });
      } catch (e) {
        if (moveRef.current !== move) {
          return;
        }
        moveRef.current = null;
        setSeatMove(null);
        declinedMovesRef.current.add(id);
        mlog(
          e instanceof MoveGoneError
            ? `MOVE ${id} REFUSED by the server (${e.phase}) — staying on ${
                from.leaseId
              }${e.hint ? ` — ${e.hint}` : ''}`
            : `MOVE ${id} claim FAILED: ${(e as Error).message} — staying on ${
                from.leaseId
              }`,
        );
        return;
      }
      if (moveRef.current !== move) {
        // Aborted while the claim was in flight (the current seat ended) — the new seat goes straight back.
        releasedRef.current.add(claimed.leaseId);
        void MytaStreamsService.releaseLease(claimed.leaseId, {
          reason: 'move-failed',
        });
        return;
      }
      move.lease = claimed;
      move.progress = { ...move.progress, phase: 'connecting' };
      publish();
      mlog(
        `MOVE ${id} CLAIMED lease ${claimed.leaseId} on ${claimed.nodeId}/${claimed.slotIndex} — opening a hidden second stream to ${claimed.streamerId} @ ${claimed.signallingUrl}`,
      );

      // The hidden peer, built by a function because it may have to be built again: on the first enforce window of
      // the deployed hub (2026-09-15 15:39Z) the destination's streamer was LISTED at the gateway but never answered
      // the subscribe — no offer for 108 s — and the move waited out its whole deadline for nothing. The main peer
      // has the KI-37 watchdog for exactly this; the hidden one gets its sibling: no data channel within
      // MOVE_OFFER_TIMEOUT_MS of the streamer being listed → disconnect, build a fresh instance, try again, at most
      // MOVE_MAX_PEER_REBUILDS times inside the same deadline.
      let shadowRebuilds = 0;
      let dataChannelUp = false;
      let streamerSeenAt: number | null = null;
      let initTimer: ReturnType<typeof setTimeout> | null = null;
      let resolutionSent = false;
      let teardownShadow: (() => void) | null = null;
      let sendInitRef: ((nudge: boolean) => Promise<void>) | null = null;
      const buildShadow = (): PixelStreaming => {
        const config = new Config({
          useUrlParams: false,
          initialSettings: {
            AutoConnect: true,
            AutoPlayVideo: true,
            StartVideoMuted: true,
            HoveringMouse: true,
            TimeoutIfIdle: false,
            MatchViewportRes: false,
            MaxReconnectAttempts: 100,
            AFKTimeout: 3600,
            ForceTURN: true,
            WaitForStreamer: true,
            WebRTCMinBitrate: 8000,
            KeyboardInput: false,
            MouseInput: false,
            TouchInput: false,
            GamepadInput: false,
            StreamerId: claimed.streamerId,
            ss: claimed.signallingUrl,
          },
        });
        const stream = new PixelStreaming(config);
        Object.assign(stream.videoElementParent.style, SHADOW_PEER_STYLE);
        stream.videoElementParent.setAttribute('data-myta-shadow-peer', id);
        document.body.appendChild(stream.videoElementParent);
        move.stream = stream;
        dataChannelUp = false;
        streamerSeenAt = null;
        resolutionSent = false;

        const muteShadow = () => {
          const video = stream.videoElementParent.querySelector('video');
          if (video) {
            video.muted = true;
            video.style.width = '100%';
            video.style.height = '100%';
            video.style.objectFit = 'contain';
          }
        };
        // KI-70 on the second peer too: the hidden element is viewport-sized, so the capped resolution it asks for
        // is the one the visible element will need — the switch then costs UE no r.SetRes.
        const sendResolution = () => {
          if (resolutionSent) {
            return;
          }
          const { clientWidth, clientHeight } = stream.videoElementParent;
          const target = capStreamResolution(clientWidth, clientHeight);
          if (target && stream.emitCommand(toResolutionCommand(target))) {
            resolutionSent = true;
          }
        };

        const sendInit = async (nudge: boolean) => {
          if (moveRef.current !== move) {
            return;
          }
          const message = await initMessageHandler({ sessionKey });
          if (moveRef.current !== move) {
            return;
          }
          if (!message) {
            mlog(
              `MOVE ${id} INIT payload unavailable for the new seat (player-session mint empty) — retry in ${
                MOVE_INIT_RETRY_MS / 1000
              } s`,
            );
            initTimer = setTimeout(
              () => void sendInit(nudge),
              MOVE_INIT_RETRY_MS,
            );
            return;
          }
          stream.emitUIInteraction(message);
          setTimeout(() => {
            if (moveRef.current === move) {
              stream.emitUIInteraction(message);
            }
          }, 1000);
          move.progress = {
            ...move.progress,
            phase: 'entering',
            initSentAt: nudge ? move.progress.initSentAt : Date.now(),
            nudged: nudge || move.progress.nudged,
          };
          publish();
          mlog(
            `MOVE ${id} INIT ${
              nudge ? 're-sent' : 'sent'
            } to the new seat under session key ${sessionKey} — waiting for the venue there (the hub keeps the current session's key until the switch)`,
          );
          MytaverseLogger.log(`INIT ${JSON.stringify(message)}`);
        };
        sendInitRef = sendInit;

        const onDataChannelOpen = () => {
          dataChannelUp = true;
          mlog(`MOVE ${id} data channel OPEN on the new seat`);
          sendResolution();
          void sendInit(false);
        };
        const onWebRtcConnected = () =>
          mlog(`MOVE ${id} webRTC CONNECTED on the new seat`);
        const onVideoInitialized = () => {
          muteShadow();
          sendResolution();
        };
        const onPlayStream = () => muteShadow();
        const onStats = (e: Event) => {
          const inbound = (
            e as unknown as { data?: { aggregatedStats?: IMediaStatsReport } }
          ).data?.aggregatedStats?.inboundVideoStats;
          const decoded = Number(
            inbound?.framesDecoded ?? inbound?.framesReceived ?? 0,
          );
          if (decoded > move.progress.frames) {
            const first = move.progress.frames === 0;
            move.progress = { ...move.progress, frames: decoded };
            if (first) {
              publish();
              mlog(
                `MOVE ${id} FIRST FRAME decoded on the new seat (${
                  inbound?.frameWidth ?? '?'
                }x${inbound?.frameHeight ?? '?'})`,
              );
            }
          }
          sendResolution();
        };
        const onStreamerList = (e: Event) => {
          const d = (
            e as unknown as {
              data?: {
                messageStreamerList?: { ids?: string[] };
                wantedStreamerId?: string;
              };
            }
          ).data;
          const ids = d?.messageStreamerList?.ids ?? [];
          const wanted = d?.wantedStreamerId ?? claimed.streamerId;
          if (ids.includes(wanted) && streamerSeenAt === null) {
            streamerSeenAt = Date.now();
          }
          mlog(
            `MOVE ${id} streamer list on the new seat [${
              ids.join(',') || 'empty'
            }] wanted=${wanted} ${
              ids.includes(wanted) ? 'PRESENT' : 'MISSING'
            }`,
          );
        };
        const onWebRtcFailed = () =>
          mlog(
            `MOVE ${id} webRTC FAILED on the new seat — the deadline decides`,
          );
        const onWebRtcDisconnected = () =>
          mlog(`MOVE ${id} webRTC disconnected on the new seat`);
        stream.addEventListener('dataChannelOpen', onDataChannelOpen);
        stream.addEventListener('webRtcConnected', onWebRtcConnected);
        stream.addEventListener('videoInitialized', onVideoInitialized);
        stream.addEventListener('playStream', onPlayStream);
        stream.addEventListener('statsReceived', onStats);
        stream.addEventListener('streamerListMessage', onStreamerList);
        stream.addEventListener('webRtcFailed', onWebRtcFailed);
        stream.addEventListener('webRtcDisconnected', onWebRtcDisconnected);
        teardownShadow = () => {
          stream.removeEventListener('dataChannelOpen', onDataChannelOpen);
          stream.removeEventListener('webRtcConnected', onWebRtcConnected);
          stream.removeEventListener('videoInitialized', onVideoInitialized);
          stream.removeEventListener('playStream', onPlayStream);
          stream.removeEventListener('statsReceived', onStats);
          stream.removeEventListener('streamerListMessage', onStreamerList);
          stream.removeEventListener('webRtcFailed', onWebRtcFailed);
          stream.removeEventListener(
            'webRtcDisconnected',
            onWebRtcDisconnected,
          );
          if (initTimer) {
            clearTimeout(initTimer);
            initTimer = null;
          }
        };
        return stream;
      };

      buildShadow();
      move.cleanup.push(() => teardownShadow?.());

      // The hidden peer's own watchdog (see buildShadow): a listed streamer that sends no offer is rebuilt, not waited on.
      const offerWatch = setInterval(() => {
        if (
          moveRef.current !== move ||
          dataChannelUp ||
          streamerSeenAt === null
        ) {
          return;
        }
        if (Date.now() - streamerSeenAt < MOVE_OFFER_TIMEOUT_MS) {
          return;
        }
        if (shadowRebuilds >= MOVE_MAX_PEER_REBUILDS) {
          streamerSeenAt = null;
          mlog(
            `MOVE ${id} still no data channel after ${MOVE_MAX_PEER_REBUILDS} hidden-peer rebuilds — the deadline decides`,
          );
          return;
        }
        shadowRebuilds += 1;
        mlog(
          `MOVE ${id} streamer listed but no offer within ${
            MOVE_OFFER_TIMEOUT_MS / 1000
          } s — rebuilding the hidden peer (${shadowRebuilds}/${MOVE_MAX_PEER_REBUILDS})`,
        );
        teardownShadow?.();
        const old = move.stream;
        if (old) {
          try {
            old.disconnect();
          } catch (e) {
            console.warn('Failed to disconnect the move peer', e);
          }
          old.videoElementParent.remove();
        }
        buildShadow();
      }, 1000);
      move.cleanup.push(() => clearInterval(offerWatch));

      // Lease B is kept alive on its own fast cadence for the length of the move, and its renew is the second,
      // server-side venue proof: `sessionReady` reads the slot's live map, so it holds even if this page missed
      // UE's announcement. A definitive refusal on it means the new seat is gone — abort, nothing to release.
      const renewTimer = setInterval(async () => {
        if (moveRef.current !== move) {
          return;
        }
        const renewed = await MytaStreamsService.renewLease(claimed.leaseId, {
          lastInputAt: new Date(lastInputRef.current).toISOString(),
          hidden: hiddenRef.current,
        });
        if (moveRef.current !== move || renewed === 'network_error') {
          return;
        }
        if (
          !renewed ||
          isSessionEnded(renewed) ||
          isLeaseGone(renewed) ||
          isLeaseRefused(renewed)
        ) {
          abortMove(
            `the new seat's lease ${claimed.leaseId} was refused (${
              !renewed
                ? 'renew rejected'
                : isSessionEnded(renewed)
                ? `session ended: ${renewed.cause}`
                : isLeaseGone(renewed)
                ? `node gone: ${renewed.cause ?? '-'}`
                : `not current: ${renewed.cause ?? '-'}`
            })`,
            { releaseSeat: false },
          );
          return;
        }
        if (
          renewed.sessionReady &&
          !renewed.restarting &&
          !move.progress.entered
        ) {
          move.progress = { ...move.progress, entered: true };
          publish();
          mlog(
            `MOVE ${id} — the server reports the new seat is in the venue (map ${
              renewed.mapName ?? '-'
            })`,
          );
        }
      }, MOVE_RENEW_INTERVAL_MS);
      move.cleanup.push(() => clearInterval(renewTimer));

      let ticks = 0;
      const watch = setInterval(() => {
        if (moveRef.current !== move) {
          return;
        }
        const now = Date.now();
        const v = moveVerdict(move.progress, deadlineMs, now);
        if (v.action === 'wait') {
          ticks += 1;
          if (ticks % 10 === 0) {
            mlog(
              `MOVE ${id} waiting — ${describeMoveView(moveView(move), now)}`,
            );
          }
          return;
        }
        if (v.action === 'nudge') {
          mlog(`MOVE ${id} ${v.why}`);
          void sendInitRef?.(true);
          return;
        }
        if (v.action === 'abort') {
          abortMove(v.why);
          return;
        }
        switchToMovedSeat(move, v.why);
      }, MOVE_WATCH_POLL_MS);
      move.cleanup.push(() => clearInterval(watch));
    },
    [
      currentEvent,
      userId,
      user,
      eventBuildUri,
      initMessageHandler,
      mlog,
      abortMove,
      switchToMovedSeat,
    ],
  );

  // Read through a ref by the renew loop, so a re-created `beginMove` (its deps move whenever the INIT payload's
  // inputs do) never restarts the renew interval.
  const beginMoveRef = useRef(beginMove);
  useEffect(() => {
    beginMoveRef.current = beginMove;
  }, [beginMove]);

  // The entry ladder's live half (entryWatch.ts), and the only watchdog in this page that judges ENTRY rather
  // than MEDIA. The media watchdog below cannot help here by construction: a UE parked on its login map streams
  // a flawless 30 fps, so frames say "healthy" while the user is looking at a screen they cannot use. This one
  // asks the only question that matters — is the user actually in? — and answers a no with, in order: send the
  // INIT again, hand the seat back for a fresh one, and finally stop and say so rather than loop.
  //
  // `enteredRef` is the exit: set by UE's TELEPORT_FINISHED or by a renew answering `sessionReady`, whichever
  // arrives first. Everything here is deliberately per SEAT, so a handoff or a UE restart re-arms it.
  useEffect(() => {
    if (!enabled || !lease || sessionEnd) {
      return;
    }

    const timer = setInterval(() => {
      if (enteredRef.current) {
        return;
      }

      const now = Date.now();
      // A restart we were told to wait for, bounded (Ivan, 2026-09-14: one minute). Kept ahead of the entry
      // verdict because while the streamer is away there is nothing an INIT could reach.
      const away = streamerReturnVerdict(streamerAwaySinceRef.current, now);

      if (away.action === 'move') {
        streamerAwaySinceRef.current = null;
        ueRestartingRef.current = false;
      }

      const verdict =
        away.action === 'move' ? away : entryVerdict(entryRef.current, now);

      if (verdict.action === 'wait') {
        return;
      }

      if (verdict.action === 'nudge') {
        entryRef.current = { ...entryRef.current, nudged: true };
        mlog(`ENTRY ${verdict.why} — ${where()}`);
        stickyEntryRef.current = true;
        setEntryNotice(describeEntry({ stage: 'nudging' }));
        // Re-arming both is what actually re-sends it: the INIT effect is gated on `initMessageSended`, and
        // bumping the attempt re-runs it even if the latch was already false for another reason.
        setInitMessageSended(false);
        setInitAttempt((attempt) => attempt + 1);

        return;
      }

      if (releasedRef.current.has(lease.leaseId)) {
        return;
      }

      if (verdict.action === 'fail') {
        mlog(`ENTRY GIVING UP: ${verdict.why} — ${where()}`);
        stickyEntryRef.current = true;
        setEntryNotice(describeEntry({ stage: 'stalled' }));
        seatOutcomeRef.current = seatOutcomeRef.current ?? {
          outcome: 'released:never-framed',
          at: now,
        };
        trackIncident(
          lease,
          AnalyticsType.EventSessionTimeout,
          `entry stalled — ${verdict.why}`,
        );
        setCutReason(MytaCutReason.NeverFramed);
        // The seat goes back either way: the user is not getting in on it, and holding it would bill a GPU for
        // a loading screen. Released by hand rather than through `abandonSlot`, because this is the one exit
        // that must NOT ask the ladder for another seat — `endSession` is what stops the loop and says so.
        void MytaStreamsService.releaseLease(lease.leaseId, {
          reason: 'never-framed',
        }).catch(() => undefined);
        void endSession(lease, {
          ended: true,
          cause: 'entry-stalled',
          why: verdict.why,
        });

        return;
      }

      const attempt = entryRef.current.rescues + 1;
      entryRef.current = { ...entryRef.current, rescues: attempt };
      mlog(
        `ENTRY ${
          verdict.why
        } (rescue ${attempt}/${MAX_ENTRY_RESCUES}) — ${where()}`,
      );
      stickyEntryRef.current = true;
      setEntryNotice(describeEntry({ stage: 'moving', attempt: attempt + 1 }));
      // The loader comes back over the dead picture for the same reason a spot handoff raises it: the next
      // thing the user would otherwise see is the login map of a machine we are already walking away from.
      setReconnecting(isPlayButtonPressedRef.current);
      seatOutcomeRef.current = seatOutcomeRef.current ?? {
        outcome: 'released:never-framed',
        at: now,
      };
      trackIncident(
        lease,
        AnalyticsType.EventSessionTimeout,
        `entry stalled — ${verdict.why}`,
      );
      setCutReason(MytaCutReason.NeverFramed);
      void abandonSlot(lease, { reason: 'never-framed' });
    }, ENTRY_WATCH_POLL_MS);

    return () => clearInterval(timer);
  }, [
    enabled,
    lease,
    sessionEnd,
    abandonSlot,
    endSession,
    setInitMessageSended,
    trackIncident,
    mlog,
    where,
  ]);

  // The one exit from the ladder above, taken by whichever proof lands first. UE's TELEPORT_FINISHED is the
  // fast one; a renew answering `sessionReady` is the one that still arrives when this page missed the
  // announcement (a re-attach, a throttled tab) — it reads the slot's own map server-side, so unlike frames it
  // cannot be fooled by a UE streaming its login screen.
  const confirmEntered = useCallback(
    (how: string) => {
      if (enteredRef.current) {
        return;
      }

      enteredRef.current = true;
      stickyEntryRef.current = false;
      mlog(`ENTRY confirmed — the user is in the venue (${how}) — ${where()}`);
      setEntryNotice(null);
      setReconnecting(false);
    },
    [mlog, where],
  );

  useEffect(() => {
    if (lease && teleportFinished) {
      confirmEntered('UE announced TELEPORT_FINISHED');
    }
  }, [lease, teleportFinished, confirmEntered]);

  // The ordinary road to the venue, narrated. Four steps, each with what is happening, how long it should take
  // and what the user has to do (nothing) — because the entry that worries people is not the broken one, it is
  // the normal one that spends forty seconds saying nothing. The ladder's own messages are sticky and outrank
  // this, and everything stops the moment the user is actually in.
  useEffect(() => {
    if (
      !enabled ||
      stickyEntryRef.current ||
      enteredRef.current ||
      sessionEnd
    ) {
      return;
    }

    const stage = !lease
      ? leasePhase?.provisioning || leasePhase?.etaSec
        ? 'starting'
        : 'searching'
      : dataChannelOpened
      ? 'entering'
      : 'connecting';

    setEntryNotice(describeEntry({ stage, etaSec: leasePhase?.etaSec }));
  }, [
    enabled,
    lease,
    leaseStatus,
    leasePhase,
    dataChannelOpened,
    sessionEnd,
    initAttempt,
  ]);

  // One line per transition, because the failure this screen can cause is invisible from the outside: a loader
  // held over a live stream and a loader held over a seat that never came back look identical to the user.
  useEffect(() => {
    mlog(
      `STEP reconnect loader ${
        reconnecting ? 'UP' : 'DOWN'
      } — playStream=${playStream} dataChannel=${dataChannelOpened} lease=${
        leaseIdRef.current ?? '-'
      } status=${leaseStatus}`,
    );
  }, [reconnecting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Second, independent way out of the reconnect loader. The first (an advancing decoded frame in the watchdog's
  // stats handler) is the one that knows a PICTURE is back, but it is one signal on one code path, and a loader
  // that outlives the stream behind it is worse than the black screen it replaced — the user is locked out of a
  // session that is running. So: once the transport is demonstrably up on the new peer (data channel open AND the
  // video element playing), give the frame path a moment to speak and then lift the loader anyway. Deliberately
  // NOT a plain timeout since `reconnecting` was raised: while the ladder is still hunting a seat there is nothing
  // to show, and a timer would drop the user back onto the very black screen this exists to prevent.
  useEffect(() => {
    if (!reconnecting || !playStream || !dataChannelOpened) {
      return;
    }
    const timer = setTimeout(() => {
      mlog(
        'STEP reconnect loader lifted on the transport, not on a frame — data channel open and video playing, but no decoded frame was reported; showing the stream rather than holding the loader over it',
      );
      setReconnecting(false);
    }, RECONNECT_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [reconnecting, playStream, dataChannelOpened, mlog]);

  // The "moving you…" / "machine reclaimed" banner stays until the NEW seat actually plays; a rebalance notice on a
  // live stream is not "moving" and is left alone (the next renew re-asserts or clears it).
  useEffect(() => {
    if (playStream && movingRef.current) {
      movingRef.current = false;
      setSpotNotice(null);
    }
  }, [playStream]);

  useEffect(() => {
    if (!lease) {
      return;
    }

    // Saved here as well as in adoptLease: the previous lease's cleanup below clears the stored lease, and a
    // packing move goes A → B with no `null` in between, so a save made before the switch would be wiped.
    if (currentEvent?.eventId) {
      MytaStreamsService.saveStoredLease(currentEvent.eventId, lease);
    }

    let fastTimer: ReturnType<typeof setInterval> | null = null;

    // What this page tells the server about the human, on every renew (myta-streams api/seat-policy.ts).
    const activity = () => ({
      lastInputAt: new Date(lastInputRef.current).toISOString(),
      hidden: hiddenRef.current,
    });

    // Every renew answer, on the 45 s cadence and on the 5 s cadence a live interruption notice switches on.
    const handleRenewed = async (
      renewed: Awaited<ReturnType<typeof MytaStreamsService.renewLease>>,
    ): Promise<void> => {
      if (!renewed || renewed === 'network_error') {
        mlog(
          `RENEW ${
            renewed === 'network_error' ? 'network error' : 'REFUSED'
          } on ${lease.leaseId} — ${where()}`,
        );
        return;
      }
      if (isSessionEnded(renewed)) {
        // The visit is over (seat cap / inactivity / operator kick). Terminal by contract — the ONE
        // refusal this page must not answer with a new lease, or the rule it enforces closes nothing.
        // Deliberately ahead of the two reseat branches below so it can never be mistaken for one.
        mlog(
          `SESSION ENDED (${renewed.cause}) on ${lease.leaseId} — ${
            renewed.why ?? 'no reason given'
          } — returning to the events list — ${where()}`,
        );
        drainRef.current = null;
        setDrainNotice(null);
        seatOutcomeRef.current = {
          outcome: `ended:${renewed.cause}`,
          status: 403,
          error: 'session_ended',
          cause: renewed.cause,
          reason: renewed.why,
          at: Date.now(),
        };
        trackIncident(
          lease,
          AnalyticsType.EventSessionTimeout,
          `session ended — ${renewed.cause}${
            renewed.why ? ` (${renewed.why})` : ''
          }`,
        );
        await endSession(lease, renewed);
        return;
      }
      if (isLeaseRefused(renewed)) {
        // The seat is not ours any more (its hold lapsed, an operator freed it, another session took it).
        // Treated exactly like 409 node_gone, because it is just as definitive — the only difference is that
        // the box is alive, so the wording must not claim a machine died.
        //
        // This used to only latch the verdict and leave the ladder alone, which was wrong in the case that
        // hides itself (found on dev 2026-09-09): the UE on that slot comes back on the SAME streamerId, the
        // library re-subscribes by itself (AutoConnect, 100 attempts), the picture returns, and the media
        // watchdog — which judges by frames — is satisfied. So nothing noticed that we were streaming on a
        // place the pool had already handed back: it can be sold to the next user at any moment (and their
        // grant restarts the UE under us), while our visit stayed metered against a seat that no longer
        // existed. Only this answer can tell us; the pixels cannot.
        if (handoffRef.current) {
          return;
        }
        handoffRef.current = true;
        movingRef.current = true;
        mlog(
          `RENEW REFUSED (${renewed.error}${
            renewed.cause ? `, cause=${renewed.cause}` : ''
          }${
            renewed.lastEvent
              ? ` — ${renewed.lastEvent.actor} at ${renewed.lastEvent.at}: ${
                  renewed.lastEvent.reason ?? '-'
                }`
              : ''
          }${
            renewed.slotEvent
              ? ` [slot since: ${renewed.slotEvent.actor} at ${renewed.slotEvent.at}]`
              : ''
          }) on ${lease.leaseId} — taking a fresh lease now — ${where()}`,
        );
        drainRef.current = null;
        setDrainNotice(null);
        setSpotNotice(describeSeatLost(renewed.cause));
        seatOutcomeRef.current = {
          outcome: `lost:not_current:${
            renewed.cause ?? renewed.lastEvent?.actor ?? 'unknown'
          }`,
          status: 404,
          error: renewed.error,
          cause: renewed.cause,
          actor: renewed.lastEvent?.actor,
          reason: renewed.lastEvent?.reason,
          at: Date.now(),
        };
        const framedBeforeSeatLoss = everFramedRef.current;
        trackIncident(
          lease,
          framedBeforeSeatLoss
            ? AnalyticsType.EventSessionCrash
            : AnalyticsType.EventSessionTimeout,
          `seat lost — no longer ours (${
            renewed.cause ?? renewed.lastEvent?.actor ?? 'unknown'
          })`,
        );
        setCutReason(
          framedBeforeSeatLoss
            ? MytaCutReason.SeatLost
            : MytaCutReason.NeverFramed,
        );
        // `release: false` — this lease is already not the slot's, so a DELETE would only 404 back.
        await abandonSlot(lease, false);
        return;
      }
      if (isLeaseGone(renewed)) {
        // The box is gone (spot reclaimed, stopped, terminated): the seat is already released server-side. Take a
        // fresh lease NOW instead of waiting for the media watchdog to notice frozen frames.
        if (handoffRef.current) {
          return;
        }
        handoffRef.current = true;
        movingRef.current = true;
        mlog(
          `RENEW node GONE${renewed.cause ? ` (${renewed.cause})` : ''} on ${
            lease.leaseId
          }${
            renewed.hint ? ` — ${renewed.hint}` : ''
          } — taking a fresh lease now — ${where()}`,
        );
        drainRef.current = null;
        setDrainNotice(null);
        setSpotNotice(describeGone(renewed.cause));
        // The one seat loss the server states outright. Reported as the hand-back it is, and the cut is
        // latched before abandonSlot drops the on-screen signal.
        seatOutcomeRef.current = {
          outcome: `lost:node_gone:${renewed.cause ?? 'unknown'}`,
          status: 409,
          error: 'node_gone',
          cause: renewed.cause,
          at: Date.now(),
        };
        const framedBeforeLoss = everFramedRef.current;
        trackIncident(
          lease,
          framedBeforeLoss
            ? AnalyticsType.EventSessionCrash
            : AnalyticsType.EventSessionTimeout,
          `seat lost — node gone (${renewed.cause ?? 'unknown'})`,
        );
        setCutReason(
          framedBeforeLoss ? MytaCutReason.SeatLost : MytaCutReason.NeverFramed,
        );
        await abandonSlot(lease, false);
        return;
      }
      const now = Date.now();
      // The visit countdown. `warn` inside the server's warnSec of the deadline is what the UI turns into
      // "your session ends in N min"; a human dismisses it by doing anything at all, since that moves
      // `lastInputAt` on the very next renew.
      setSessionLife(renewed.session ?? null);
      // The server's own answer to "is this user actually in?" — it reads the slot's live map, so it is the
      // one proof that survives a page which missed UE's announcement. Only ever confirms, never denies: a
      // renew that says `false` on a venue we are still loading is simply early.
      if (renewed.sessionReady && !renewed.restarting) {
        confirmEntered('the server reports this slot is in the venue');
      }
      // The packing move (seatMove.ts): the pool proposes a ready seat elsewhere for this user. Guards inside.
      const proposal = proposalFromRenew(renewed, now);
      if (proposal) {
        void beginMoveRef.current(proposal, lease);
      }
      const notice = noticeFromRenew(renewed, now, drainRef.current);
      serverViewRef.current = {
        mapName: renewed.mapName ?? null,
        sessionReady: renewed.sessionReady ?? null,
        at: now,
      };
      if (
        renewed.lastEvent &&
        renewed.lastEvent.at !== ueEventRef.current?.at
      ) {
        ueEventRef.current = renewed.lastEvent;
        mlog(
          `RENEW ok — slot event under this seat: ${
            renewed.lastEvent.actor
          } at ${renewed.lastEvent.at}${
            renewed.lastEvent.reason ? ` (${renewed.lastEvent.reason})` : ''
          } — ${where()}`,
        );
      }
      if (
        notice &&
        (!drainRef.current || drainRef.current.kind !== notice.kind)
      ) {
        mlog(
          `SPOT ${
            notice.kind === 'interrupt'
              ? 'INTERRUPTION NOTICE'
              : 'rebalance recommendation'
          } for ${lease.nodeId}${
            notice.deadline
              ? ` — EC2 acts at ${new Date(notice.deadline).toISOString()}`
              : ''
          } replacement=${notice.replacement?.status ?? '-'} — ${where()}`,
        );
      }
      drainRef.current = notice;
      setDrainNotice(notice);
      if (!notice) {
        if (!movingRef.current) {
          setSpotNotice(null);
        }
        mlog(
          `RENEW ok ttl=${renewed.ttlSec ?? '-'}s step=${
            stepRef.current
          } frames=${mediaRef.current.frames} — ${where()}`,
        );
        return;
      }
      if (handoffRef.current) {
        return;
      }
      setSpotNotice(describeNotice(notice, now));
      mlog(
        `RENEW ok (draining: ${notice.kind}, replacement=${
          notice.replacement?.status ?? '-'
        }${
          notice.deadline
            ? `, ${Math.max(
                0,
                Math.round((notice.deadline - now) / 1000),
              )}s left`
            : ''
        }) — ${where()}`,
      );
      if (notice.kind === 'interrupt' && !fastTimer) {
        fastTimer = setInterval(async () => {
          await handleRenewed(
            await MytaStreamsService.renewLease(lease.leaseId, activity()),
          );
        }, DRAINING_RENEW_INTERVAL_MS);
      }
      const decision = decideHandoff(notice, now);
      if (decision.action === 'handoff') {
        handoffRef.current = true;
        movingRef.current = true;
        mlog(
          `SPOT HANDOFF: ${decision.reason} — leaving ${lease.nodeId}/${
            lease.slotIndex
          } for a new seat — ${where()}`,
        );
        drainRef.current = null;
        setDrainNotice(null);
        setSpotNotice(describeHandoffInProgress(decision.reason));
        seatOutcomeRef.current = {
          outcome: 'released:spot-handoff',
          status: 200,
          error: 'spot-handoff',
          cause: decision.reason,
          at: now,
        };
        const framedBeforeMove = everFramedRef.current;
        trackIncident(
          lease,
          framedBeforeMove
            ? AnalyticsType.EventSessionCrash
            : AnalyticsType.EventSessionTimeout,
          `seat released — spot handoff (${decision.reason})`,
        );
        setCutReason(
          framedBeforeMove ? MytaCutReason.SeatLost : MytaCutReason.NeverFramed,
        );
        await abandonSlot(lease, { reason: 'spot-handoff' });
      }
    };

    const renewInterval = setInterval(async () => {
      await handleRenewed(
        await MytaStreamsService.renewLease(lease.leaseId, activity()),
      );
    }, LEASE_RENEW_INTERVAL_MS);

    // Reload/close survival (BUG-2 2026-07-22): beforeunload must NOT release — an F5 that released
    // sent the slot through a restart cycle (60-90 s re-entry, and the ladder could start a whole GPU
    // node for the returning user). The stored lease lets the reloaded tab rebind to the same slot;
    // a genuinely closed tab is reclaimed by the server-side lease TTL (~120 s, T5-proven). Deliberate
    // in-app exits (event switch / back to the events list) unmount this provider and DO release.
    const leaseEventId = currentEvent?.eventId;
    const handleBeforeUnload = () => {
      if (leaseEventId) {
        MytaStreamsService.saveStoredLease(leaseEventId, lease);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(renewInterval);
      if (fastTimer) {
        clearInterval(fastTimer);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (!releasedRef.current.has(lease.leaseId)) {
        MytaStreamsService.releaseLease(lease.leaseId, {
          keepalive: true,
          reason: 'exit',
        });
      }
      if (leaseEventId) {
        MytaStreamsService.clearStoredLease(leaseEventId);
      }
      if (leaseIdRef.current === lease.leaseId) {
        leaseIdRef.current = null;
      }
    };
  }, [lease, abandonSlot, mlog, where, trackIncident]);

  useEffect(() => {
    if (!target) {
      return;
    }

    // A packing move hands over its already-connected, already-framing peer instead of asking for a new one — the
    // cleanup of the previous run has just disconnected the old peer, which is the whole make-before-break.
    const pending = adoptPeerRef.current;
    if (
      pending &&
      pending.streamerId === target.streamerId &&
      pending.signallingUrl === target.signallingUrl
    ) {
      adoptPeerRef.current = null;
      stepRef.current = 'moved-peer-adopted';
      mlog(
        `STEP adopting the pre-connected peer of a packing move — ${pending.streamerId} @ ${pending.signallingUrl} (no new signalling connection)`,
      );
      setPixelStreaming(pending.stream);
      setRootElement(pending.stream.videoElementParent);
      return () => {
        try {
          pending.stream.disconnect();
        } catch (e) {
          console.warn('Failed to disconnect myta pixel streaming', e);
        }
      };
    }

    Logger.InitLogging(LogLevel.Error, false);

    const config = new Config({
      useUrlParams: false,
      initialSettings: {
        AutoConnect: true,
        AutoPlayVideo: false,
        StartVideoMuted: false,
        HoveringMouse: true,
        TimeoutIfIdle: false,
        // KI-70: off. With it on, the lib sized the stream to the browser viewport (r.SetRes 3440x1271 on a
        // slot budgeted for 1920x1080); the capped, aspect-preserving sender below owns the Resolution command.
        MatchViewportRes: false,
        MaxReconnectAttempts: 100,
        AFKTimeout: 3600,
        ForceTURN: true,
        WaitForStreamer: true,
        WebRTCMinBitrate: 8000,
        StreamerId: target.streamerId,
        ss: target.signallingUrl,
      },
    });

    stepRef.current = 'signalling-connecting';
    wsStateRef.current = 'connecting';
    mlog(
      `STEP connecting signalling (attempt ${rebuildsRef.current + 1}/${
        MAX_PEER_REBUILDS + 1
      } on this lease) — ${target.streamerId} @ ${
        target.signallingUrl
      } ForceTURN=true WaitForStreamer=true`,
    );

    const stream = new PixelStreaming(config);
    stream.videoElementParent.style.width = '100%';
    stream.videoElementParent.style.height = '100%';

    setPixelStreaming(stream);
    setRootElement(stream.videoElementParent);

    return () => {
      try {
        stream.disconnect();
      } catch (e) {
        console.warn('Failed to disconnect myta pixel streaming', e);
      }
    };
  }, [target, peerRebuild]);

  // A key or a mouse button held when the tab/window loses focus is released for UE by hand — the library
  // never sees the release that happens elsewhere, and the avatar walks (or the camera drags) on forever.
  // Keyed off the visible peer, so a packing-move adoption and a KI-37 rebuild both re-arm it on the stream
  // the user is actually looking at.
  useReleaseHeldInput(pixelStreaming);

  // KI-37 diagnostics. Every line here exists because its absence cost real debugging time: the failure looks
  // like silence, and silence was read as "the peer connection closed" for days. `streamerListMessage` is the
  // decisive one — it says whether the streamer we are waiting for is even registered.
  useEffect(() => {
    if (!pixelStreaming) {
      return;
    }

    const onStreamerList = (e: Event) => {
      const d = (
        e as unknown as {
          data?: {
            messageStreamerList?: { ids?: string[] };
            wantedStreamerId?: string;
            autoSelectedStreamerId?: string;
          };
        }
      ).data;
      const ids = d?.messageStreamerList?.ids ?? [];
      const wanted = d?.wantedStreamerId ?? '-';
      const present = ids.includes(wanted);
      // The live slot status the restart wait is driven by — the gateway republishes this list whenever a
      // streamer registers or drops, so it says when our UE is actually back without anyone timing it.
      streamerPresentRef.current = present;
      // The cap's clock (entryWatch.streamerReturnVerdict). Started the first list that does not carry our
      // streamer — whatever the reason, a restart we asked for or a UE that died on its own — and cleared the
      // moment it is back, so a slow-framing but registered streamer is never judged by it.
      if (!present) {
        streamerAwaySinceRef.current =
          streamerAwaySinceRef.current ?? Date.now();
      } else {
        streamerAwaySinceRef.current = null;
      }
      // "Back" only counts once we have seen it GO. The restart is dispatched asynchronously, so the first
      // streamer list after the rebind still lists the slot that is about to die — treating that as the
      // return ended the wait ~1 s in and handed the next 15 s to the ordinary first-frame budget, which the
      // restart then spent.
      if (!present && ueRestartingRef.current) {
        ueRestartSawDropRef.current = true;
      }
      if (present && ueRestartingRef.current && ueRestartSawDropRef.current) {
        ueRestartingRef.current = false;
        mediaRef.current = { frames: 0, at: Date.now() };
        rebuildsRef.current = 0;
        mlog(
          `STEP streamer ${wanted} is back after the UE restart — judging this entry like any other from here`,
        );
      }
      stepRef.current = present ? 'streamer-found' : 'streamer-missing';
      // Receiving ANY message proves the gateway leg is fine, which is the distinction that matters:
      // `ws=connecting` in a heartbeat means we never heard from the gateway at all (network/ALB/TG), while
      // `ws=open` + `streamer-missing` is KI-37 proper — the slot's streamer is not registered.
      wsStateRef.current = 'open';
      // THE decisive line for KI-37: if our streamer is not in this list, the frontend will wait for it
      // silently and forever, and nothing else in the system can tell us that is what is happening.
      mlog(
        `STEP streamer list [${ids.join(',') || 'empty'}] wanted=${wanted} ${
          present ? 'PRESENT' : 'MISSING — the frontend will wait for it'
        } autoSelected=${d?.autoSelectedStreamerId ?? '-'}`,
      );
    };
    const onSubscribeFailed = (e: Event) => {
      stepRef.current = 'subscribe-failed';
      mlog(
        `STEP subscribe FAILED: ${String(
          (e as unknown as { data?: { message?: string } }).data?.message ?? '',
        )} — ${where()}`,
      );
    };
    const onWebRtcConnecting = () => {
      wsStateRef.current = 'open';
      stepRef.current = 'webrtc-connecting';
      iceStateRef.current = 'connecting';
      mlog(`STEP webRTC connecting — ${where()}`);
    };
    const onWebRtcFailed = () => {
      stepRef.current = 'webrtc-failed';
      iceStateRef.current = 'failed';
      mlog(`STEP webRTC FAILED — ${where()}`);
    };
    const onWebRtcDisconnected = (e: Event) => {
      stepRef.current = 'webrtc-disconnected';
      iceStateRef.current = 'disconnected';
      mlog(
        `STEP webRTC disconnected: ${String(
          (e as unknown as { data?: { eventString?: string } }).data
            ?.eventString ?? '',
        )} — ${where()}`,
      );
    };
    const onDataChannelClose = () => {
      stepRef.current = 'data-channel-closed';
      mlog(`STEP data channel CLOSED — ${where()}`);
    };
    const onPlayerCount = (e: Event) => {
      mlog(
        `STEP streamer reports ${String(
          (e as unknown as { data?: { count?: number } }).data?.count ?? '?',
        )} player(s) on this slot — ${where()}`,
      );
    };

    pixelStreaming.addEventListener('webRtcConnecting', onWebRtcConnecting);
    pixelStreaming.addEventListener('playerCount', onPlayerCount);
    pixelStreaming.addEventListener('streamerListMessage', onStreamerList);
    pixelStreaming.addEventListener('subscribeFailed', onSubscribeFailed);
    pixelStreaming.addEventListener('webRtcFailed', onWebRtcFailed);
    pixelStreaming.addEventListener('webRtcDisconnected', onWebRtcDisconnected);
    pixelStreaming.addEventListener('dataChannelClose', onDataChannelClose);

    return () => {
      pixelStreaming.removeEventListener(
        'webRtcConnecting',
        onWebRtcConnecting,
      );
      pixelStreaming.removeEventListener('playerCount', onPlayerCount);
      pixelStreaming.removeEventListener('streamerListMessage', onStreamerList);
      pixelStreaming.removeEventListener('subscribeFailed', onSubscribeFailed);
      pixelStreaming.removeEventListener('webRtcFailed', onWebRtcFailed);
      pixelStreaming.removeEventListener(
        'webRtcDisconnected',
        onWebRtcDisconnected,
      );
      pixelStreaming.removeEventListener(
        'dataChannelClose',
        onDataChannelClose,
      );
    };
  }, [pixelStreaming, mlog, where]);

  // KI-37 watchdog: the media path is the only thing that proves this session works, so it is the only thing
  // worth watching. Lease renewals prove nothing (the server's view was correct in every recorded case).
  useEffect(() => {
    if (!pixelStreaming || !target || !lease) {
      return;
    }

    const startedAt = Date.now();
    mediaRef.current = { frames: 0, at: startedAt };
    netRef.current = { ...EMPTY_NET_SNAPSHOT };
    // A new lease and a peer rebuild both rebuild the client, so the signalling
    // socket is new and the previous streamer's absence says nothing about this
    // one — carrying it over blames a fresh slot for the dead one's silence.
    streamerPresentRef.current = null;

    let framedLogged = false;
    const onStats = (e: Event) => {
      const stats = (
        e as unknown as { data?: { aggregatedStats?: IMediaStatsReport } }
      ).data;
      const aggregated = stats?.aggregatedStats;
      const inbound = aggregated?.inboundVideoStats;
      const decoded = Number(
        inbound?.framesDecoded ?? inbound?.framesReceived ?? 0,
      );

      netRef.current = nextNetSnapshot(netRef.current, aggregated, Date.now());
      if (inbound) {
        videoRef.current = videoShapeOf(aggregated);
      }

      if (decoded > mediaRef.current.frames) {
        mediaRef.current = { frames: decoded, at: Date.now() };
        everFramedRef.current = true;
        // The picture is back — but only once it is on SCREEN. PixelStreamingWrapper does not put the new
        // element in the DOM until `playStream` latches, and a reseat frames ~half a second before its
        // resumer plays it, so dropping the loader on the frame alone would uncover the frozen last frame of
        // the seat we just left. Re-checked every report instead of latched, for exactly that reason. A
        // decoded frame and not `playStream` alone, because a peer rebuild reuses the latched flag and its
        // one-shot event never fires again.
        //
        // …and a frame is no longer enough on its own (2026-09-14). The first frames of a fresh machine are
        // its UE LOGIN SCREEN: dropping the loader here is what showed a user the login map and the engine's
        // debug widget after a spot handoff, which reads as a broken session rather than a working one. So the
        // loader now waits for the seat to be confirmed in the venue (`confirmEntered`), with the
        // RECONNECT_SETTLE_MS backstop above as the only other way down — a peer rebuild on a seat already in
        // the venue keeps the old behaviour, because `enteredRef` is still set for it.
        if (playStreamRef.current && enteredRef.current) {
          setReconnecting(false);
        }
        // Written once per peer: the handler runs every second, and a state write
        // on each frame would re-render the whole event subtree.
        // Both, and whichever lands second decides: UE can answer after the first frame, and it can
        // never answer at all. Re-checked every report, so this settles within a second of UE returning.
        if (!pictureLiveRef.current && ue5ConnectedRef.current) {
          pictureLiveRef.current = true;
          setPictureLive(true);
          mlog(`picture live — first decoded frame confirmed — ${where()}`);
          // Cleared here, not in `handlePlayStream`: that fires once per element and a rebuild
          // reuses it, so a second identical cut was a no-op write and closed as UNKNOWN_FAILURE.
          setCutReason(null);
        }
        if (!framedLogged) {
          framedLogged = true;
          if (rebuildsRef.current === 0) {
            firstPeerFramedRef.current = true;
          } else {
            rebuildsFramedRef.current += 1;
          }
          stepRef.current = 'streaming';
          const sinceLease = leaseAtRef.current
            ? `${
                Math.round((Date.now() - leaseAtRef.current) / 100) / 10
              }s after the lease`
            : 'timing unknown';
          mlog(
            `STEP FIRST FRAME decoded (${inbound?.frameWidth ?? '?'}x${
              inbound?.frameHeight ?? '?'
            }) — ${sinceLease} — ${where()}`,
          );
        }
      }
    };
    pixelStreaming.addEventListener('statsReceived', onStats);

    // Live view while the entry is in flight: which step, how long in it, what the media path has produced.
    // Silent once the stream runs — the stall branch below is what watches a running session.
    const heartbeat = setInterval(() => {
      if (mediaRef.current.frames > 0) {
        return;
      }
      mlog(
        `WAITING step=${stepRef.current} ws=${wsStateRef.current} dc=${
          dataChannelOpened ? 'open' : 'closed'
        } frames=0 attempt=${rebuildsRef.current + 1}/${
          MAX_PEER_REBUILDS + 1
        } — ${where()}`,
      );
    }, ENTRY_HEARTBEAT_MS);

    const giveUpSlot = async (what: string) => {
      const dead = lease.leaseId;
      // Releasing takes a round trip the 1 s timer keeps ticking through, so the
      // set that already marks a lease released on purpose is claimed up front.
      // `abandonSlot` adds to the same set, so this stays the early-out.
      if (releasedRef.current.has(dead)) {
        return;
      }

      mlog(
        `WATCHDOG ${what} — releasing lease ${dead} after ${MAX_PEER_REBUILDS} failed rebuilds, asking the ladder for another slot — ${where()}`,
      );
      // Read off the latch, never off `mediaRef`: the last rebuild emptied that
      // counter, so a visit of twenty minutes would report as never framed.
      const everFramed = everFramedRef.current;
      // A seat loss the server already named stays; otherwise this is the watchdog's own hand-back.
      // No `status`/`error`: those describe a refusal the server sent, and nothing
      // refused us here — a zero would read as a measured one.
      seatOutcomeRef.current = seatOutcomeRef.current ?? {
        outcome: everFramed ? 'released:give-up' : 'released:never-framed',
        at: Date.now(),
      };

      trackIncident(
        lease,
        everFramed
          ? AnalyticsType.EventSessionCrash
          : AnalyticsType.EventSessionTimeout,
        `${what} — the slot was handed back after ${MAX_PEER_REBUILDS} rebuilds`,
      );
      // Set before `abandonSlot` drops `playStream`, so billing closes this
      // segment as a cut rather than falling back to an unknown failure.
      setCutReason(
        everFramed ? MytaCutReason.Stalled : MytaCutReason.NeverFramed,
      );
      // The seat is handed back through the one shared exit, naming why: the server's session row then
      // reads a watchdog give-up (framed, then dead) or a seat that never framed — not "user left".
      await abandonSlot(lease, {
        reason: everFramed ? 'give-up' : 'never-framed',
      });
    };

    let lastTickAt = Date.now();

    const timer = setInterval(() => {
      // A machine that slept wakes with a visible tab and an hours-old clock, so
      // `document.hidden` never covered it and the first tick declared a stall.
      const tickGapMs = Date.now() - lastTickAt;
      lastTickAt = Date.now();

      if (tickGapMs > TICK_SUSPENSION_MS) {
        // Credit the gap, never restart from now: discarding the idle already
        // accumulated would let a thrashing stream disarm the watchdog for good,
        // and the net stamps shift with it or every signal reads as hours dead.
        mediaRef.current = {
          ...mediaRef.current,
          at: mediaRef.current.at + tickGapMs,
        };
        netRef.current = creditNetGap(netRef.current, tickGapMs);
        mlog(
          `WATCHDOG timers did not run for ${Math.round(
            tickGapMs / 1000,
          )}s — excusing that gap rather than judging it — ${where()}`,
        );

        return;
      }

      // A hidden tab is throttled by the browser: frames legitimately stop, and rebuilding then would break a
      // session that is merely in the background.
      if (typeof document !== 'undefined' && document.hidden) {
        mediaRef.current = { ...mediaRef.current, at: Date.now() };
        netRef.current = creditNetGap(netRef.current, tickGapMs);
        return;
      }

      // A restart the server told us about is not a stall. Waiting on the live signal rather than a timer:
      // as long as our streamer is not in the gateway's list, the UE is still coming up and there is nothing
      // for the watchdog to judge. Reclaiming here would spend the seat the restart exists to keep.
      if (ueRestartingRef.current && !streamerPresentRef.current) {
        mediaRef.current = { ...mediaRef.current, at: Date.now() };
        netRef.current = creditNetGap(netRef.current, tickGapMs);
        return;
      }

      // Measured from `at`, which starts at the effect's own start and is re-stamped whenever the clock must
      // not run — a hidden tab, or a UE restart we were told to wait through. Using the effect start directly
      // would declare a slot dead the instant its streamer came back from a restart we had just waited out.
      const { frames, at } = mediaRef.current;
      const idleMs = Date.now() - at;
      const stuck =
        frames > 0
          ? idleMs > MEDIA_STALL_TIMEOUT_MS
          : idleMs > FIRST_FRAME_TIMEOUT_MS;
      if (!stuck) {
        return;
      }

      const what =
        frames > 0
          ? `stream stalled at ${frames} frames for ${Math.round(
              idleMs / 1000,
            )}s`
          : `no decoded frame in ${Math.round(idleMs / 1000)}s`;

      if (rebuildsRef.current < MAX_PEER_REBUILDS) {
        rebuildsRef.current += 1;
        mlog(
          `WATCHDOG ${what} — rebuilding the peer on the SAME lease (rebuild ${
            rebuildsRef.current
          }/${MAX_PEER_REBUILDS}) — ${where()}`,
        );
        // Degradation without a new session, which is what an issue means: the
        // seat is kept and only the peer is rebuilt.
        trackIncident(
          lease,
          AnalyticsType.EventSessionIssue,
          `${what} — rebuilding the peer (${rebuildsRef.current}/${MAX_PEER_REBUILDS})`,
          'peer-rebuild',
        );
        // The picture is gone until the new peer frames, so the segment ends here and the dead
        // stretch falls between two invoices instead of inside one. Reason first, or the close
        // falls back to `UNKNOWN_FAILURE` and the per-reason breakdown is wrong.
        setCutReason(MytaCutReason.Stalled);
        pictureLiveRef.current = false;
        setPictureLive(false);
        mlog(`picture live reset — media stalled — ${where()}`);
        mediaRef.current = { frames: 0, at: Date.now() };
        netRef.current = { ...EMPTY_NET_SNAPSHOT };
        setDataChannelOpened(false);
        setInitMessageSended(false);
        // Unlike a reseat, `playStream` stays latched here (the element is replaced, the lib's one-shot event
        // is in the past), so nothing else in the UI can tell that the picture went away — say it explicitly.
        setReconnecting(isPlayButtonPressedRef.current);
        setPeerRebuild((n) => n + 1);
        return;
      }

      void giveUpSlot(what);
    }, 1000);

    return () => {
      clearInterval(timer);
      clearInterval(heartbeat);
      pixelStreaming.removeEventListener('statsReceived', onStats);
    };
  }, [
    pixelStreaming,
    trackIncident,
    target,
    lease,
    peerRebuild,
    abandonSlot,
    dataChannelOpened,
    mlog,
    where,
  ]);

  const handleDataChannelOpened = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    stepRef.current = 'data-channel-open';
    mlog(`STEP data channel OPEN — ${where()}`);

    if (!hoveringMouseIntervalRef.current) {
      hoveringMouseIntervalRef.current = setInterval(() => {
        pixelStreaming.config.setSettings({
          HoveringMouse: true,
        });
      }, 1000);
    }

    setDataChannelOpened(true);
  }, [pixelStreaming]);

  const handleWebRtcConnected = useCallback(async () => {
    stepRef.current = 'webrtc-connected';
    wsStateRef.current = 'open';
    iceStateRef.current = 'connected';
    mlog(`STEP webRTC CONNECTED — ${where()}`);
    setInitMessageSended(false);
    setDataChannelOpened(false);
    setConnectedSlot(target?.streamerId || '');

    if (!userId || !currentEvent) {
      return;
    }

    try {
      await PixelStreamingLogsService.createPixelStreamingLog({
        participantId: userId,
        eventId: currentEvent.eventId,
        timestamp: Date.now(),
        pixelStreamingProvider: 'MYTA_STREAMS',
        pixelStreamingProviderData: {
          id: target?.streamerId || '',
        },
      });
    } catch (e) {
      console.warn('Failed to create myta pixel streaming log', e);
    }
  }, [userId, currentEvent, target, setInitMessageSended]);

  const handleInitializeButton = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    pixelStreaming.config.setSettings({
      HoveringMouse: true,
    });

    const video = pixelStreaming.videoElementParent.querySelector('video');
    if (video) {
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'contain';
    }

    stepRef.current = 'video-initialized';
    mlog(`STEP video track INITIALIZED — ${where()}`);
    setMytaLoading(false);
  }, [pixelStreaming, mlog, where]);

  const handlePlayStream = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    if (env.REACT_APP_MYTAVERSE_ENV !== 'PROD') {
      (pixelStreaming as any).requestShowFps?.();
    }

    pixelStreaming.config.setSettings({
      HoveringMouse: true,
    });

    stepRef.current = 'playing';
    mlog(
      `STEP playStream — video is playing, ${
        Math.round((Date.now() - (leaseAtRef.current || Date.now())) / 100) / 10
      }s after the lease — ${where()}`,
    );
    setPlayStream(true);
    setGameReadyToPlay(true);
    // Back to null on every start, so a second cut carrying the same reason is
    // still a state change and still reaches the latch.
    setCutReason(null);
    // Deliberately NOT where the reconnect loader is dismissed — see the stats handler. This fires in the
    // same commit as `setPlayStream(true)`, one paint before the wrapper's effect puts the new element in
    // the DOM, so lifting the loader here shows the frozen last frame of the seat we left.

    if (!isPlayButtonPressedRef.current) {
      isPlayButtonPressedRef.current = true;
    }
  }, [pixelStreaming, setGameReadyToPlay]);

  const handleSettingChanged = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    if (!pixelStreaming.config.getSettings().HoveringMouse) {
      pixelStreaming.config.setSettings({
        HoveringMouse: true,
      });
    }
  }, [pixelStreaming]);

  const handleResize = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    pixelStreaming.config.setSettings({
      HoveringMouse: true,
    });
  }, [pixelStreaming]);

  useEffect(() => {
    if (!pixelStreaming) {
      return;
    }

    pixelStreaming.addEventListener('videoInitialized', handleInitializeButton);
    pixelStreaming.addEventListener('settingsChanged', handleSettingChanged);
    pixelStreaming.addEventListener('dataChannelOpen', handleDataChannelOpened);
    pixelStreaming.addEventListener('playStream', handlePlayStream);
    pixelStreaming.addEventListener('webRtcConnected', handleWebRtcConnected);
    window.addEventListener('resize', handleResize);

    return () => {
      pixelStreaming.removeEventListener(
        'videoInitialized',
        handleInitializeButton,
      );
      pixelStreaming.removeEventListener(
        'settingsChanged',
        handleSettingChanged,
      );
      pixelStreaming.removeEventListener(
        'dataChannelOpen',
        handleDataChannelOpened,
      );
      pixelStreaming.removeEventListener('playStream', handlePlayStream);
      pixelStreaming.removeEventListener(
        'webRtcConnected',
        handleWebRtcConnected,
      );
      window.removeEventListener('resize', handleResize);
    };
  }, [
    pixelStreaming,
    handleInitializeButton,
    handleSettingChanged,
    handleDataChannelOpened,
    handlePlayStream,
    handleWebRtcConnected,
    handleResize,
  ]);

  const resolutionCapRef = useRef(createStreamResolutionCap());

  // KI-70: size the stream to the element it is shown in, never above the slot's 1080p pixel budget, both axes
  // scaled by the same factor so the browser's upscale fills the element with no bars or stretching and the
  // lib's input coordinate translation stays consistent. Same wire format as the lib's own MatchViewportRes
  // path (a `Command` message, id 51, carrying Resolution.Width/Height), so UE executes one r.SetRes WxH.
  const sendCappedResolution = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    const { clientWidth, clientHeight } = pixelStreaming.videoElementParent;
    const plan = resolutionCapRef.current.plan(clientWidth, clientHeight);
    if (plan.action !== 'send') {
      return;
    }

    // emitCommand refuses until the video element has metadata (the lib's own readiness gate). Nothing is
    // recorded as sent until it takes, so the next trigger — videoInitialized, playStream, a resize — retries.
    if (!pixelStreaming.emitCommand(toResolutionCommand(plan.target))) {
      return;
    }

    resolutionCapRef.current.markSent(plan.target);
    console.debug(
      `[myta-streams] resolution cap viewport=${clientWidth}x${clientHeight} stream=${plan.target.width}x${plan.target.height} budget=${STREAM_PIXEL_BUDGET}`,
    );
  }, [pixelStreaming]);

  useEffect(() => {
    if (!pixelStreaming || !dataChannelOpened) {
      return;
    }

    // A (re)opened data channel may front a UE that restarted at its launch resolution — start over.
    resolutionCapRef.current.reset();

    let debounce: number | null = null;
    const schedule = () => {
      if (debounce !== null) {
        window.clearTimeout(debounce);
      }
      debounce = window.setTimeout(() => {
        debounce = null;
        sendCappedResolution();
      }, RESOLUTION_CAP_DEBOUNCE_MS);
    };

    sendCappedResolution();

    // The element is measured, not the window: it is detached (0x0) until PixelStreamingWrapper mounts it, and
    // the ResizeObserver is what sees that mount land. The window listener covers the wrapper's post-mount
    // resize re-fire and browsers without ResizeObserver.
    const element = pixelStreaming.videoElementParent;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(schedule);
    observer?.observe(element);
    window.addEventListener('resize', schedule);
    pixelStreaming.addEventListener('videoInitialized', schedule);
    pixelStreaming.addEventListener('playStream', schedule);

    return () => {
      if (debounce !== null) {
        window.clearTimeout(debounce);
      }
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      pixelStreaming.removeEventListener('videoInitialized', schedule);
      pixelStreaming.removeEventListener('playStream', schedule);
    };
  }, [pixelStreaming, dataChannelOpened, sendCappedResolution]);

  // Reload race, 2026-08-11: LoadingCinema is gated on `playStream`, and `playStream` is only ever set from
  // the library's one-shot `playStream` event. On a reload REBIND the lease, the streamer and the slot all
  // pre-exist, so the video can start playing BEFORE the listener effect above commits — the event lands in
  // the past, nothing sets `playStream`, and the loader covers a stream that is already decoding. Measured
  // live: `FIRST FRAME decoded 2.8s after the lease` with no `STEP playStream` line at all, loader forever.
  // A listener cannot see the past, so ask the video element instead. Same class as the harness's
  // ensureInitSent race. Bounded poll because the element is attached a beat after the instance appears.
  // Deliberately NOT gated on `playStream`: the element can be replaced after that latched (a peer rebuild
  // brings its own videoElementParent) and the replacement arrives paused at currentTime 0, with no user
  // gesture to autoplay it. Measured 2026-08-12 on reload round 9 of 10 — srcObject set, readyState 4,
  // paused=true, t=0, loader already gone: a frozen picture and no way for the user to notice why. So keep a
  // cheap resumer alive for the session instead of only until the first play.
  useEffect(() => {
    // The gate that makes the loader's Enter button real: on a fresh entry this effect used to be the
    // actual auto-enter (its unmuted play() started the world and ADOPT latched playStream long before
    // the user saw anything), so it may only run once the user pressed Enter — or on a reload rebind of
    // a session they had already entered (resumedSessionRef, set in adoptLease before target exists, so
    // it is always decided by the time this effect can fire).
    if (!pixelStreaming || !(userEntered || resumedSessionRef.current)) {
      return;
    }

    // Pick the element that actually carries the stream. Never widen this to the document: `rootElement`
    // is deliberately NOT in the DOM until playStream flips (PixelStreamingWrapper mounts it only then),
    // and a document-wide query returns OTHER providers' tags (e.g. GameCast's `gamecastVideoTag`) — a
    // different provider entirely, whose element will never carry a MYTA_STREAMS stream.
    const liveVideo = (): HTMLVideoElement | null =>
      [...(rootElement?.querySelectorAll('video') ?? [])].find(
        (v) => !!v.srcObject && v.readyState >= 2,
      ) ?? null;

    const adoptIfAlreadyPlaying = () => {
      const video = liveVideo();
      if (!video) {
        return false;
      }
      if (video.paused || !video.currentTime) {
        // Measured live in Chrome 2026-08-12 on a reload rebind: srcObject set, readyState 4 (enough data),
        // paused=true, currentTime=0 — the stream is attached and buffered, nobody ever started it. A page
        // load after F5 carries no user activation, so the library's own play() is refused by autoplay
        // policy, its one-shot `playStream` never fires, and the loader waits on an event that cannot come.
        // Muted playback is always permitted, so prefer muted-and-visible over black-forever; the click the
        // user is about to make in-world restores sound.
        void video.play().catch(() => {
          video.muted = true;
          return video.play().catch(() => undefined);
        });
        return false;
      }
      if (!playStream) {
        mlog(
          'STEP playStream ADOPTED — video was already playing before the listeners attached (reload rebind); the one-shot event was missed',
        );
        handlePlayStream();
      }
      return true;
    };

    adoptIfAlreadyPlaying();

    const timer = window.setInterval(adoptIfAlreadyPlaying, 500);

    return () => window.clearInterval(timer);
  }, [pixelStreaming, rootElement, playStream, handlePlayStream, userEntered]);

  useEffect(() => {
    if (dataChannelOpened && !isPlayButtonPressedRef.current) {
      setGameReadyToPlay(false);
    }
  }, [dataChannelOpened, setGameReadyToPlay]);

  useEffect(() => {
    if (!enabled || !isDebugMode || initMessageSended) {
      return;
    }

    let cancelled = false;

    const resolveDebugInitMessage = async () => {
      const message = await initMessageHandler();

      if (cancelled) {
        return;
      }

      if (!message) {
        mlog(
          'STEP editor debug mode — INIT payload is not ready yet (the entry has not resolved a room or the player-session mint came back empty)',
        );
        return;
      }

      setInitMessageSended(true);
      setMytaLoading(false);
      setPlayStream(true);
      setGameReadyToPlay(true);

      mlog(
        'STEP editor debug mode — INIT payload is ready; copy it from the Developers Terminal (Ctrl+Shift+U → Config) into the local UE editor',
      );
      MytaverseLogger.log(`INIT ${JSON.stringify(message)}`);
    };

    resolveDebugInitMessage();

    return () => {
      cancelled = true;
    };
  }, [enabled, isDebugMode, initMessageHandler, initMessageSended, mlog]);

  useAsyncEffect(async () => {
    if (
      !initMessageHandler ||
      !pixelStreaming ||
      !dataChannelOpened ||
      initMessageSended
    ) {
      return;
    }

    if (initRetryTimeoutRef.current) {
      clearTimeout(initRetryTimeoutRef.current);
      initRetryTimeoutRef.current = null;
    }

    const message = await initMessageHandler();

    if (!message) {
      if (initAttempt >= 15) {
        stepRef.current = 'init-unavailable';
        mlog(
          `STEP INIT payload still unavailable after ${
            initAttempt + 1
          } attempts — giving up (player-session mint keeps failing) — ${where()}`,
        );
        return;
      }

      stepRef.current = 'init-retry';
      mlog(
        `STEP INIT payload unavailable (player-session mint empty or failed, attempt ${
          initAttempt + 1
        }) — retry in 6s — ${where()}`,
      );
      initRetryTimeoutRef.current = setTimeout(
        () => setInitAttempt((attempt) => attempt + 1),
        6000,
      );

      return;
    }

    pixelStreaming.emitUIInteraction(message);

    setTimeout(() => {
      pixelStreaming.emitUIInteraction(message);
    }, 1000);

    // KNOWN GAP, measured 2026-08-12: a UE that is already logged in keeps the websocket session it was
    // INIT-ed with, and neither a second INIT nor a WS_CONNECTION_URL over this data channel moves it (both
    // tried; the slot kept reconnecting as the previous page's session a minute later). So after a reload the
    // stream is correct while UE's control link still belongs to a page that no longer exists. Re-pointing it
    // has to come from the UE side; until then the entry is carried by the seated-room restore in
    // MytaverseEventProvider, and anything that rides that link (chat, teleport, participant presence) stays
    // bound to the old session.
    if (staleUeSessionRef.current) {
      staleUeSessionRef.current = false;
      mlog(
        `STEP re-INIT sent to a slot that was already logged in — UE keeps its previous websocket session (known UE-side gap) — ${where()}`,
      );
    }

    setInitMessageSended(true);
    // The clock the entry ladder is judged on (entryWatch.ts). An INIT that goes out is a promise that a venue
    // follows within seconds; from here on, silence is a fault rather than patience — and the page owns the
    // decision to re-send it, hand the seat back, or stop and say so.
    entryRef.current = { ...entryRef.current, initSentAt: Date.now() };
    if (!enteredRef.current) {
      setEntryNotice(
        describeEntry({
          stage: entryRef.current.nudged ? 'nudging' : 'entering',
        }),
      );
    }

    pixelStreaming.config.setSettings({
      HoveringMouse: true,
    });

    stepRef.current = 'init-sent';
    mlog(`STEP INIT sent to UE (login payload) — ${where()}`);
    MytaverseLogger.log(`INIT ${JSON.stringify(message)}`);
  }, [
    initMessageSended,
    pixelStreaming,
    dataChannelOpened,
    initMessageHandler,
    initAttempt,
  ]);

  useEffect(() => {
    if (!dataChannelOpened || !pixelStreaming) {
      return;
    }

    const message = {
      action: 'CHANGE_WORLD_SOUND',
      value: gameSound,
    };

    pixelStreaming.emitUIInteraction(JSON.stringify(message));
  }, [gameSound, dataChannelOpened, pixelStreaming]);

  const togglePlayStream = useCallback(() => {
    if (!pixelStreaming) {
      return;
    }

    setUserEntered(true);
    pixelStreaming.play();
  }, [pixelStreaming]);

  return {
    pixelStreaming,
    mytaLoading,
    dataChannelOpened,
    playStream,
    togglePlayStream,
    isPlayButtonPressed: isPlayButtonPressedRef.current,
    rootElement,
    connectedSlot,
    lease,
    leaseStatus,
    leaseError,
    leasePhase,
    cutReason,
    pictureLive,
    reconnecting,
    drainNotice,
    spotNotice,
    sessionEnd,
    sessionLife,
    afkPrompt,
    confirmPresence,
    entryNotice,
    seatMove,
    noteSessionEvent,
  };
};

export const useMytaStreams = (): IMytaStreamsContext => {
  return useMytaverseEvent().mytaStreams;
};
